import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import ContentBox from '../../components/ContentBox';
import SlotSymbol, { SYMBOL_IDS, SYMBOL_NAMES, SYMBOL_TINTS } from '../../components/slots/SlotSymbols';
import PrizeWheel from '../../components/slots/PrizeWheel';
import AudioSettings from '../../components/slots/AudioSettings';
import { holdGameToasts, releaseGameToasts } from '../../utils/toastGate';
import {
    primeAudio, initMusic, startMusic, fadeMusicOut, releaseAudio,
    playUi, playSpinStart, playReelStop, playTease, playWin, playCoins,
    playTrigger, playJackpot, playBonusIntro, playRetrigger,
    playWheelSpin, playWheelTick, playPrize, playLose, playWheelLand, playWomp,
    startReelLoop, stopReelLoop,
} from '../../utils/chiptune';
import {
    getSlotPaytable, getSlotState, startSlotDaily, slotSpin,
    slotBonusStep, slotAck, claimSlotDaily, endSlotStakes,
    getSlotLeaderboard, getSlotSubmissions, getSlotWheel, spinSlotWheel,
} from '../../services/api';

const REELS = 6;
const ROWS = 4;
const CREDITS_PER_STAMP = 1;
const CREDITS_PER_CARD = 10;

const REEL_STOP_BASE = 900;
const REEL_STOP_STEP = 380;
const SETTLE_MS = 620;
const SETTLE_SYMBOLS = 5;
const MIN_RUN_DEFAULT = 3;
const MS_PER_SYMBOL = 70;
const ANTICIPATION_BY_REEL = {
    jackpot: [0, 0, 700, 1100, 1700, 2600],
    wheel:   [0, 0, 0, 0, 0, 2400],
    scatter: [0, 0, 500, 700, 900, 1300],
    top:     [0, 0, 400, 600, 800, 1100],
};
const ANTICIPATION_BUDGET = { jackpot: 6500, wheel: 2400, scatter: 2000, top: 1600 };
const SETTLE_EASING = 'cubic-bezier(0.16, 0.84, 0.28, 1.05)';
const MIN_FILLER = 6;
const MAX_FILLER = 80;
const WIN_HOLD_MS = 1100;
const BONUS_STEP_GAP = 760;
const RETRIGGER_HOLD_MS = 1900;
const STEP_WATCHDOG_MS = 20000;
const RETRIGGER_FLASH_MS = 2100;
const WHEEL_HOLD_MS = 3400;
const TRIGGER_FLASH_MS = 1500;
const BONUS_INTRO_MS = 2000;
const TEASER_SCATTERS = 2;

const SPIN_POOL = SYMBOL_IDS.filter(s => s !== 'jackpot' && s !== 'wheel');
const ROOM_BG = `${process.env.PUBLIC_URL}/img/games/habburgersslots/room_burger_goo.png`;
const MUSIC_SRC = `${process.env.PUBLIC_URL}/img/games/habburgersslots/habburger.mp3`;

// Phones get a reduced particle budget - these run on every landed wild and
// scatter at once, so the counts matter more than the individual animations.
const IS_MOBILE = typeof navigator !== 'undefined'
    && (/Mobi|Android/i.test(navigator.userAgent)
        || (typeof window !== 'undefined' && window.matchMedia
            && window.matchMedia('(max-width: 768px)').matches));

const ALL_WILD_DRIPS = [
    { left: '22%', size: '9%', dur: 2.1, delay: 0 },
    { left: '48%', size: '12%', dur: 2.7, delay: 0.55 },
    { left: '72%', size: '8%', dur: 1.8, delay: 1.15 },
];
const WILD_DRIPS = IS_MOBILE ? ALL_WILD_DRIPS.slice(1, 2) : ALL_WILD_DRIPS;

// Long durations with a dead tail so sparks pop now and then rather than
// streaming constantly - cheaper, and reads better against pixel art.
const ALL_SCATTER_SPARKS = [
    { left: '20%', top: '58%', size: '10%', dur: 3.4, delay: 0 },
    { left: '72%', top: '64%', size: '8%', dur: 4.3, delay: 1.4 },
    { left: '48%', top: '48%', size: '7%', dur: 3.9, delay: 2.6 },
];
const SCATTER_SPARKS = IS_MOBILE ? ALL_SCATTER_SPARKS.slice(0, 1) : ALL_SCATTER_SPARKS;
const ROOM_W = 722;
const ROOM_H = 489;

const ROOM_CENTRE_X = 33;
const ROOM_CENTRE_Y = -17;

// Extra framing for the play view: up and to the right of the centred room.
const PLAY_NUDGE_X = 40;
const PLAY_NUDGE_Y = 30;

const BLANK_GRID = Array.from({ length: REELS }, (_, r) =>
    Array.from({ length: ROWS }, (_, i) => SPIN_POOL[(r * 3 + i * 2) % SPIN_POOL.length])
);

const gridPositions = (g, sym) => {
    const out = [];
    for (let r = 0; r < REELS; r++) {
        for (let i = 0; i < ROWS; i++) if (g[r] && g[r][i] === sym) out.push([r, i]);
    }
    return out;
};

const randSym = (pool) => {
    const p = pool || SPIN_POOL;
    return p[Math.floor(Math.random() * p.length)];
};

const fillerPoolFor = (finalGrid) =>
    finalGrid.some(col => col.some(c => c === 'blank'))
        ? [...SPIN_POOL, 'blank', 'blank', 'blank', 'blank', 'blank']
        : SPIN_POOL;

const WIN_TIERS = [
    { at: 25, label: 'Mega Win!', color: '#ff3fb4' },
    { at: 10, label: 'Big Win!',  color: '#ffd24a' },
    { at: 4,  label: 'Nice Win!', color: '#e6c500' },
];

const winSfxTier = (win, bet) => {
    if (!win || win <= 0 || !bet) return -1;
    const m = win / bet;
    if (m >= 25) return 3;
    if (m >= 10) return 2;
    if (m >= 4) return 1;
    return 0;
};

const winTierFor = (win, bet) => {
    if (!win || !bet) return null;
    const m = win / bet;
    return WIN_TIERS.find(t => m >= t.at) || null;
};
const fmt = n => (n ?? 0).toLocaleString('en-GB');
const cardsLabel = n => `${n} card${n !== 1 ? 's' : ''}`;
const fmtMult = m => `x${(m ?? 0) >= 100 ? Math.round(m) : (m ?? 0).toFixed(1)}`;
const posKey = (r, i) => `${r}:${i}`;

function highlightSet(lines) {
    const set = new Set();
    (lines || []).forEach(l => (l.positions || []).forEach(([r, i]) => set.add(posKey(r, i))));
    return set;
}

const PAY_IDS = SYMBOL_IDS.filter(id => id !== 'wild' && id !== 'scatter' && id !== 'wheel');

function analyseProgress(finalGrid, minRuns, landed) {
    const scatters = [];
    for (let r = 0; r < landed; r++) {
        for (let i = 0; i < ROWS; i++) {
            if (finalGrid[r][i] === 'scatter') scatters.push([r, i]);
        }
    }

    const winPositions = [];
    const winSymbols = [];

    for (const sym of PAY_IDS) {
        const need = minRuns[sym] || MIN_RUN_DEFAULT;
        let run = 0;
        while (run < landed && finalGrid[run].some(c => c === sym || c === 'wild')) run++;
        if (run < need) continue;

        winSymbols.push(sym);
        for (let r = 0; r < run; r++) {
            for (let i = 0; i < ROWS; i++) {
                if (finalGrid[r][i] === sym || finalGrid[r][i] === 'wild') winPositions.push([r, i]);
            }
        }
    }

    return { scatters, scatterCount: scatters.length, winSymbols, winPositions };
}

function teaseHighlights(finalGrid, minRuns, landed) {
    const prog = analyseProgress(finalGrid, minRuns, landed);
    const hot = new Set();
    if (prog.winPositions.length) prog.winPositions.forEach(([r, i]) => hot.add(posKey(r, i)));
    if (prog.scatterCount >= TEASER_SCATTERS) prog.scatters.forEach(([r, i]) => hot.add(posKey(r, i)));

    // A stamp only lights up while the jackpot run is still unbroken across
    // every reel that has landed. A stray stamp is just a dead symbol.
    const stamps = [];
    for (let r = 0; r < landed; r++) {
        for (let i = 0; i < ROWS; i++) if (finalGrid[r][i] === 'jackpot') stamps.push([r, i]);
    }
    if (landed >= 2 && stamps.length === landed) {
        stamps.forEach(([r, i]) => hot.add(posKey(r, i)));
    }

    const wheels = [];
    for (let r = 0; r < landed; r++) {
        for (let i = 0; i < ROWS; i++) if (finalGrid[r][i] === 'wheel') wheels.push([r, i]);
    }
    wheels.forEach(([r, i]) => hot.add(posKey(r, i)));

    return { hot, prog };
}

function aliveRun(finalGrid, sym, landed) {
    let run = 0;
    while (run < landed && finalGrid[run].some(c => c === sym || c === 'wild')) run++;
    return run;
}

function teaseReason(finalGrid, minRuns, topSymbols, r) {
    if (r < 2) return null;

    let scatters = 0;
    let jackpots = 0;
    for (let q = 0; q < r; q++) {
        for (let i = 0; i < ROWS; i++) {
            if (finalGrid[q][i] === 'scatter') scatters++;
            if (finalGrid[q][i] === 'jackpot') jackpots++;
        }
    }

    if (jackpots >= r) return 'jackpot';
    if (r === REELS - 1 && finalGrid[0].some(c => c === 'wheel')) return 'wheel';
    if (scatters === TEASER_SCATTERS) return 'scatter';

    const top = topSymbols.some(sym => {
        const need = minRuns[sym] || MIN_RUN_DEFAULT;
        return r >= need && aliveRun(finalGrid, sym, r) === r;
    });
    return top ? 'top' : null;
}

function planStops(finalGrid, minRuns, topSymbols) {
    const reasons = [];
    for (let r = 0; r < REELS; r++) reasons.push(teaseReason(finalGrid, minRuns, topSymbols, r));

    // The deepest live run decides how much drama the whole spin is worth, and
    // the hold grows reel by reel so the deciding reel gets the longest one.
    let budget = 0;
    for (const reason of reasons) {
        if (reason) budget = Math.max(budget, ANTICIPATION_BUDGET[reason] || 0);
    }

    const stops = [];
    const teasing = [];
    const antic = [];
    let t = REEL_STOP_BASE;

    for (let r = 0; r < REELS; r++) {
        const reason = reasons[r];
        const want = reason ? (ANTICIPATION_BY_REEL[reason][r] || 0) : 0;
        const add = Math.max(0, Math.min(want, budget));

        teasing.push(!!reason && add > 0);
        antic.push(add);
        if (add > 0) { t += add; budget -= add; }

        stops.push(t);
        t += REEL_STOP_STEP;
    }

    return { stops, teasing, antic, reasons, endsAt: stops[REELS - 1] + 200 };
}

export default function SlimeSpins({ userData }) {
    const [paytable, setPaytable] = useState(null);
    const [economy, setEconomy] = useState(null);
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);

    const [mode, setMode] = useState('daily');
    const [screen, setScreen] = useState('title');
    const betCards = 1;

    const [grid, setGrid] = useState(BLANK_GRID);
    const [reels, setReels] = useState(() =>
        BLANK_GRID.map(col => ({ strip: [...col], offset: 0, transition: 'none' })));
    const [landedReel, setLandedReel] = useState(-1);
    const [winTier, setWinTier] = useState(null);
    const [waysWon, setWaysWon] = useState(0);
    const [teaseInfo, setTeaseInfo] = useState(null);
    const [jackpot, setJackpot] = useState(null);
    const [spinningReels, setSpinningReels] = useState([false, false, false, false, false, false]);
    const [teaseReels, setTeaseReels] = useState([false, false, false, false, false, false]);
    const [busy, setBusy] = useState(false);
    const [lastWin, setLastWin] = useState(0);
    const [bannerWin, setBannerWin] = useState(0);
    const [highlights, setHighlights] = useState(new Set());
    const [wildMults, setWildMults] = useState({});
    const [message, setMessage] = useState('');
    const [retriggerFlash, setRetriggerFlash] = useState(0);

    const [bonusIntro, setBonusIntro] = useState(null);
    const [bonus, setBonus] = useState(null);
    const [bonusSummary, setBonusSummary] = useState(null);
    const [claimResult, setClaimResult] = useState(null);

    const [leaderboard, setLeaderboard] = useState([]);
    const [lbPeriod, setLbPeriod] = useState('alltime');
    const [submissions, setSubmissions] = useState([]);
    const [showPaytable, setShowPaytable] = useState(false);
    const [showAdmin, setShowAdmin] = useState(false);
    const [cellPx, setCellPx] = useState(64);
    const [areaSize, setAreaSize] = useState({ w: ROOM_W, h: ROOM_H });
    const gridRef = useRef(null);
    const areaRef = useRef(null);
    const [forceKind, setForceKind] = useState(null);
    const [forceCount, setForceCount] = useState(3);

    const [wheel, setWheel] = useState(null);
    const [wheelSegments, setWheelSegments] = useState([]);
    const [wheelResult, setWheelResult] = useState(null);
    const [wheelSpinning, setWheelSpinning] = useState(false);
    const [wheelRevealed, setWheelRevealed] = useState(false);
    const [giftPrompt, setGiftPrompt] = useState(null);
    const giftPromptSeenRef = useRef(false);
    const spentWheelsRef = useRef(new Set());
    const toastHoldRef = useRef(false);
    const [payingNotice, setPayingNotice] = useState(false);
    const hadGiftedRef = useRef(false);
    const [flash, setFlash] = useState(null);

    const timersRef = useRef([]);
    const mountedRef = useRef(true);
    const spinningRef = useRef(spinningReels);
    const animatingRef = useRef(false);
    const betCreditsRef = useRef(CREDITS_PER_CARD);
    const minRunsRef = useRef({});
    const topSymbolsRef = useRef([]);
    const landedWheelsRef = useRef(new Set());
    const teasedRef = useRef(false);

    useEffect(() => { spinningRef.current = spinningReels; }, [spinningReels]);

    useEffect(() => {
        const el = gridRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => {
            const w = el.clientWidth;
            if (w > 0) setCellPx(w / REELS);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [screen]);

    useEffect(() => {
        const el = areaRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => {
            if (el.clientWidth > 0) setAreaSize({ w: el.clientWidth, h: el.clientHeight });
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const isGuest = !userData;

    const clearTimers = useCallback(() => {
        timersRef.current.forEach(t => clearTimeout(t));
        timersRef.current = [];
        animatingRef.current = false;
    }, []);

    const later = useCallback((fn, ms) => {
        const t = setTimeout(() => { if (mountedRef.current) fn(); }, ms);
        timersRef.current.push(t);
        return t;
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        initMusic(MUSIC_SRC);
        return () => {
            mountedRef.current = false;
            clearTimers();
            releaseAudio();
        };
    }, [clearTimers]);

    const refreshBoards = useCallback(async (period) => {
        try {
            const [lb, subs] = await Promise.all([
                getSlotLeaderboard(period || lbPeriod),
                getSlotSubmissions(),
            ]);
            if (!mountedRef.current) return;
            if (lb?.success) setLeaderboard(lb.leaderboard || []);
            if (subs?.success) setSubmissions(subs.submissions || []);
        } catch (e) { /* boards are non-critical */ }
    }, [lbPeriod]);

    const refreshState = useCallback(async () => {
        try {
            const res = await getSlotState();
            if (mountedRef.current && res?.success) setState(res);
            return res;
        } catch (e) {
            return null;
        }
    }, []);

    const holdToasts = useCallback(() => {
        if (toastHoldRef.current) return;
        toastHoldRef.current = true;
        holdGameToasts();
    }, []);

    const releaseToasts = useCallback(() => {
        if (!toastHoldRef.current) return;
        toastHoldRef.current = false;
        releaseGameToasts();
    }, []);

    useEffect(() => () => { if (toastHoldRef.current) { toastHoldRef.current = false; releaseGameToasts(); } }, []);

    const openWheel = useCallback(async (source, wheelSpinId) => {
        if (wheelSpinId && spentWheelsRef.current.has(wheelSpinId)) return;
        try {
            const res = await getSlotWheel();
            if (!mountedRef.current) return;
            if (res?.pending && spentWheelsRef.current.has(res.pending.wheelSpinId)) return;
            setWheelSegments(res?.segments || []);
            setWheelResult(null);
            setWheelRevealed(false);
            setWheelSpinning(false);
            setWheel({ source: source || 'trigger' });
        } catch (e) {
            toast.error('Could not open the Slime Wheel.');
        }
    }, []);

    const handleSpinWheel = useCallback(async () => {
        if (wheelSpinning || wheelResult) return;
        setWheelSpinning(true);
        playWheelSpin();
        holdToasts();
        try {
            const res = await spinSlotWheel();
            if (!mountedRef.current) return;
            if (!res?.success) {
                releaseToasts();
                toast.error(res?.message || 'Could not spin the wheel.');
                setWheelSpinning(false);
                setWheel(null);
                setWheelResult(null);
                setWheelRevealed(false);
                await refreshState();
                return;
            }
            if (res.wheelSpinId) spentWheelsRef.current.add(res.wheelSpinId);
            if (res.segments) setWheelSegments(res.segments);
            setWheelResult(res);
            if (typeof res.balance === 'number') {
                setState(st => (st ? { ...st, stamps: { total: res.balance, cards: Math.floor(res.balance / 10), credits: res.balance * CREDITS_PER_STAMP } } : st));
            }
        } catch (e) {
            releaseToasts();
            if (mountedRef.current) {
                toast.error('Could not reach the server.');
                setWheelSpinning(false);
            }
        }
    }, [holdToasts, refreshState, releaseToasts, wheelSpinning, wheelResult]);

    const handleWheelSettled = useCallback(() => {
        setWheelSpinning(false);
        setWheelRevealed(true);
        playPrize();
        releaseToasts();
        later(async () => {
            if (!mountedRef.current) return;
            await refreshState();
            if (!mountedRef.current) return;
            setWheel(null);
            setWheelResult(null);
            setWheelRevealed(false);
        }, WHEEL_HOLD_MS);
    }, [later, refreshState, releaseToasts]);

    const flashTrigger = useCallback(async (kind, positions) => {
        if (!positions || positions.length === 0) return;
        playTrigger(kind);
        const keys = new Set(positions.map(([r, i]) => posKey(r, i)));
        setBannerWin(0);
        setWinTier(null);
        setWaysWon(0);
        setHighlights(keys);
        setFlash({ kind, keys });
        await new Promise(resolve => later(resolve, TRIGGER_FLASH_MS));
        if (!mountedRef.current) return;
        setFlash(null);
    }, [later]);

    const animateTo = useCallback((finalGrid, onDone) => {
        clearTimers();
        animatingRef.current = true;
        landedWheelsRef.current.clear();
        teasedRef.current = false;
        setHighlights(new Set());
        setWinTier(null);
        setTeaseInfo(null);
        setWildMults({});
        setBannerWin(0);
        setWaysWon(0);
        setRetriggerFlash(0);
        setSpinningReels([true, true, true, true, true, true]);
        startReelLoop();

        const minRuns = minRunsRef.current;
        const { stops, teasing, antic, endsAt } = planStops(finalGrid, minRuns, topSymbolsRef.current);
        teasedRef.current = teasing.some(Boolean);
        setTeaseReels([false, false, false, false, false, false]);

        const pool = fillerPoolFor(finalGrid);

        // Two phases per reel: a constant-speed spin, then a deceleration into
        // place. Anticipation lengthens the constant-speed phase, so a teased
        // reel keeps running at full pelt before it starts slowing.
        const built = finalGrid.map((col, r) => {
            const totalMs = stops[r];
            const settleMs = Math.min(Math.round(SETTLE_MS * (teasing[r] ? 1.5 : 1)), Math.round(totalMs * 0.45));
            const linearMs = totalMs - settleMs;
            const linearSymbols = Math.min(MAX_FILLER, Math.max(MIN_FILLER, Math.round(linearMs / MS_PER_SYMBOL)));
            const fill = linearSymbols + SETTLE_SYMBOLS;
            return {
                strip: [...col, ...Array.from({ length: fill }, () => randSym(pool))],
                fill,
                linearMs,
                settleMs,
            };
        });

        setReels(built.map(b => ({ strip: b.strip, offset: b.fill, transition: 'none' })));

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (!mountedRef.current) return;
                setReels(built.map(b => ({
                    strip: b.strip,
                    offset: SETTLE_SYMBOLS,
                    transition: `transform ${b.linearMs}ms linear`,
                })));
            });
        });

        built.forEach((b, r) => {
            later(() => {
                setReels(prev => {
                    const next = [...prev];
                    next[r] = {
                        strip: b.strip,
                        offset: 0,
                        transition: `transform ${b.settleMs}ms ${SETTLE_EASING}`,
                    };
                    return next;
                });
            }, b.linearMs);
        });

        for (let r = 0; r < REELS; r++) {
            if (teasing[r]) {
                later(() => {
                    setTeaseReels(prev => { const n = [...prev]; n[r] = true; return n; });
                    playTease();
                }, Math.max(0, stops[r] - antic[r] * 0.95));
            }
            later(() => {
                setSpinningReels(prev => { const n = [...prev]; n[r] = false; return n; });
                setTeaseReels(prev => { const n = [...prev]; n[r] = false; return n; });
                setLandedReel(r);
                if (r === REELS - 1) stopReelLoop();

                if (r < REELS - 1) {
                    const { hot, prog } = teaseHighlights(finalGrid, minRuns, r + 1);
                    setHighlights(hot);
                    setTeaseInfo(hot.size
                        ? { symbols: prog.winSymbols.length, scatters: prog.scatterCount, reels: r + 1 }
                        : null);
                }
            }, stops[r]);
        }

        later(() => {
            stopReelLoop();
            if (!mountedRef.current) return;
            animatingRef.current = false;
            setGrid(finalGrid);
            setTeaseReels([false, false, false, false, false, false]);
            setLandedReel(-1);
            setTeaseInfo(null);
            onDone && onDone();
        }, endsAt);
    }, [clearTimers, later]);

    useEffect(() => {
        (async () => {
            try {
                const pt = await getSlotPaytable();
                if (mountedRef.current && pt?.success) {
                    setPaytable(pt.paytable);
                    setEconomy(pt.economy);
                    const map = {};
                    (pt.paytable.symbols || []).forEach(sym => {
                        if (sym.pays) map[sym.id] = sym.minRun || MIN_RUN_DEFAULT;
                    });
                    minRunsRef.current = map;
                    topSymbolsRef.current = (pt.paytable.symbols || [])
                        .filter(sym => sym.pays && sym.pays[6])
                        .sort((a, b) => b.pays[6] - a.pays[6])
                        .slice(0, 2)
                        .map(sym => sym.id);
                }
            } catch (e) { /* paytable is non-critical */ }

            const st = await refreshState();
            await refreshBoards();
            if (!mountedRef.current) return;
            setLoading(false);
            if (isGuest || st?.daily?.state === 'claimed') setMode('stakes');
            else setMode('daily');

            if (st?.pendingWheel) {
                await openWheel(st.pendingWheel.source, st.pendingWheel.wheelSpinId);
            }

            if (st?.outstanding) {
                const o = st.outstanding;
                setScreen('playing');
                setMode(o.mode);
                if (o.spin?.grid) setGrid(o.spin.grid);
                if (o.state === 'bonus' && o.bonus) {
                    setMessage('You have an unfinished bonus round - resume it to continue.');
                    setBonus({
                        phase: 'interrupted',
                        spinId: o.spinId,
                        bonusId: o.bonus.bonusId,
                        isSuper: o.bonus.isSuper,
                        totalSpins: o.bonus.totalSpins,
                        stepNumber: o.bonus.stepsRevealed,
                        running: o.bonus.running,
                        multiplier: 1,
                    });
                } else if (o.state === 'pending') {
                    slotAck(o.spinId).then(refreshState).catch(() => {});
                }
            }
        })();
    }, [isGuest, openWheel, refreshBoards, refreshState]);

    useEffect(() => { refreshBoards(lbPeriod); }, [lbPeriod, refreshBoards]);

    useEffect(() => {
        if (isGuest) return undefined;
        return () => { endSlotStakes().catch(() => {}); };
    }, [isGuest]);

    useEffect(() => {
        if (animatingRef.current) return;
        if (spinningReels.some(Boolean)) return;
        setReels(grid.map(col => ({ strip: [...col], offset: 0, transition: 'none' })));
    }, [grid, spinningReels]);

    // Pixel art only ever scales by whole numbers, background included.
    const bgFit = Math.max(1, Math.ceil(Math.max(areaSize.w / ROOM_W, areaSize.h / ROOM_H)));
    const onTitle = screen === 'title';

    useEffect(() => {
        if (loading) return;
        if (onTitle) fadeMusicOut(900); else startMusic();
    }, [loading, onTitle]);
    const bgZoom = onTitle ? 1 : bgFit + 1;
    const bgX = (ROOM_CENTRE_X + (onTitle ? 0 : PLAY_NUDGE_X)) * bgZoom;
    const bgY = (ROOM_CENTRE_Y + (onTitle ? 0 : PLAY_NUDGE_Y)) * bgZoom;

    useEffect(() => {
        if (!state?.pendingWheel || wheel || busy || bonus) return;
        if (spentWheelsRef.current.has(state.pendingWheel.wheelSpinId)) return;
        openWheel(state.pendingWheel.source, state.pendingWheel.wheelSpinId);
    }, [state, wheel, busy, bonus, openWheel]);

    useEffect(() => {
        if (mode !== 'stakes' || screen !== 'playing' || giftPromptSeenRef.current) return;
        const gifts = state?.gifts;
        if (gifts && (gifts.freeSpins > 0 || gifts.bonusRounds > 0)) {
            giftPromptSeenRef.current = true;
            setGiftPrompt(gifts);
        }
    }, [mode, screen, state]);

    const isAdmin = !!state?.isAdmin;
    const forceKinds = state?.forceKinds || [];
    const daily = state?.daily;
    const balance = state?.stamps?.total ?? 0;
    const balanceCards = state?.stamps?.cards ?? 0;
    const betCredits = betCards * CREDITS_PER_CARD;
    const betStamps = betCredits / CREDITS_PER_STAMP;
    const dailySpinsLeft = daily?.spinsLeft ?? (economy?.dailySpins ?? 20);
    const dailyClaimable = !isGuest && daily?.state === 'active' && dailySpinsLeft === 0;
    const dailyDone = daily?.state === 'claimed';
    const machineClosed = state?.gameEnabled === false;
    const wheelSize = Math.max(180, Math.min(320, Math.floor(Math.min(areaSize.w * 0.78, areaSize.h * 0.58))));
    const freeSpinsLeft = state?.gifts?.freeSpins ?? 0;
    const usingGiftedSpin = mode === 'stakes' && !isGuest && freeSpinsLeft > 0;
    const freeSpinBadge = isGuest
        ? null
        : mode === 'daily'
            ? (daily?.state === 'active' && dailySpinsLeft > 0
                ? { label: 'Daily Free Spins', left: dailySpinsLeft }
                : null)
            : (usingGiftedSpin ? { label: 'Free Spins', left: freeSpinsLeft } : null);

    useEffect(() => {
        if (mode !== 'stakes' || isGuest) {
            hadGiftedRef.current = usingGiftedSpin;
            return;
        }
        if (hadGiftedRef.current && !usingGiftedSpin) setPayingNotice(true);
        hadGiftedRef.current = usingGiftedSpin;
    }, [usingGiftedSpin, mode, isGuest]);


    const runBonusSteps = useCallback(async (bonusId, isSuper, totalSpins) => {
        let done = false;
        let guard = 0;
        let running = 0;
        let pendingBalance = null;

        setBonus({ phase: 'playing', bonusId, isSuper, totalSpins, stepNumber: 1, running: 0, multiplier: 1 });

        while (!done && guard < 150 && mountedRef.current) {
            guard += 1;
            let res;
            try {
                res = await slotBonusStep(bonusId);
            } catch (e) {
                setMessage('Connection dropped mid-bonus - press Resume, your round is saved.');
                setBonus(b => ({ ...(b || {}), phase: 'interrupted', bonusId, isSuper }));
                return;
            }
            if (!res?.success) {
                setMessage(res?.message || 'Bonus step failed.');
                setBonus(b => ({ ...(b || {}), phase: 'interrupted', bonusId, isSuper }));
                return;
            }
            if (!res.step) { running = res.total ?? running; break; }

            const step = res.step;

            setBonus(b => ({
                ...(b || {}),
                phase: 'playing',
                bonusId,
                isSuper,
                stepNumber: step.stepNumber,
                totalSpins: step.totalSteps,
            }));

            await new Promise(resolve => {
                let settled = false;
                const finish = () => { if (!settled) { settled = true; resolve(); } };
                const watchdog = setTimeout(finish, STEP_WATCHDOG_MS);

                animateTo(step.grid, () => {
                    if (!mountedRef.current) { clearTimeout(watchdog); return finish(); }
                    setWildMults(step.wildMults || {});
                    const stepHot = highlightSet(step.lines);
                    if (step.retrigger) {
                        (step.grid || []).forEach((col, r) => col.forEach((sym, i) => {
                            if (sym === 'scatter') stepHot.add(posKey(r, i));
                        }));
                    }
                    setHighlights(stepHot);
                    setBannerWin(step.win);
                    if (step.win > 0) setLastWin(step.win);
                    setWaysWon((step.lines || []).reduce((n, l) => n + (l.ways || 0), 0));
                    setWinTier(step.jackpotHit ? null : winTierFor(step.win, betCreditsRef.current));
                    if (step.jackpotHit) { playJackpot(); setJackpot({ amount: step.jackpotWin, mode }); }
                    setBonus(b => ({
                        ...(b || {}),
                        phase: 'playing',
                        bonusId,
                        isSuper,
                        totalSpins: step.totalSteps,
                        stepNumber: step.stepNumber,
                        multiplier: step.multiplier,
                        running: step.running,
                    }));
                    if (step.retrigger) {
                        setRetriggerFlash(step.retrigger);
                        playRetrigger();
                        later(() => setRetriggerFlash(0), RETRIGGER_FLASH_MS);
                    }
                    const stepTier = winSfxTier(step.win, betCreditsRef.current);
                    if (stepTier >= 0 && !step.jackpotHit) {
                        playWin(stepTier);
                        playCoins(2 + stepTier * 3);
                    } else if (teasedRef.current && !step.jackpotHit && !step.retrigger && !step.wheelTriggered) {
                        playWomp();
                    }
                    if (typeof res.balance === 'number') pendingBalance = res.balance;
                    later(
                        () => { clearTimeout(watchdog); finish(); },
                        step.retrigger ? RETRIGGER_HOLD_MS : (step.win > 0 ? WIN_HOLD_MS : BONUS_STEP_GAP),
                    );
                });
            });

            if (step.wheelTriggered) {
                await flashTrigger('wheel', step.wheelPositions || gridPositions(step.grid, 'wheel'));
                if (!mountedRef.current) return;
            }

            running = step.running;
            done = res.done;
            if (done) running = res.total ?? running;
        }

        if (!mountedRef.current) return;
        setWildMults({});
        setBonus(null);
        setBonusSummary({ isSuper, total: running });
        if (typeof pendingBalance === 'number') {
            setState(st => (st ? { ...st, stamps: { total: pendingBalance, cards: Math.floor(pendingBalance / 10), credits: pendingBalance * CREDITS_PER_STAMP } } : st));
        }
        await refreshState();
        if (mode === 'daily') refreshBoards();
    }, [animateTo, flashTrigger, later, mode, refreshBoards, refreshState]);

    const startBonus = useCallback((info) => {
        playBonusIntro(info.isSuper);
        setBonusIntro({
            isSuper: info.isSuper,
            spins: info.totalSpins,
            scatterCount: info.scatterCount,
            bonusId: info.bonusId,
        });
    }, []);

    const confirmBonus = useCallback(async () => {
        const info = bonusIntro;
        if (!info) return;
        setBonusIntro(null);
        await runBonusSteps(info.bonusId, info.isSuper, info.spins);
    }, [bonusIntro, runBonusSteps]);

    const handleSpin = useCallback(async () => {
        if (busy) return;
        primeAudio();
        playSpinStart();
        setBusy(true);
        setMessage('');
        setBonusSummary(null);
        setClaimResult(null);
        setBannerWin(0);
        setWaysWon(0);
        setWildMults({});

        try {
            betCreditsRef.current = mode === 'daily' ? CREDITS_PER_CARD : betCards * CREDITS_PER_CARD;
            if (mode === 'stakes' && !isGuest && !usingGiftedSpin) {
                setState(st => (st && st.stamps
                    ? { ...st, stamps: { ...st.stamps, total: Math.max(0, st.stamps.total - betStamps), cards: Math.floor(Math.max(0, st.stamps.total - betStamps) / 10) } }
                    : st));
            }
            const res = await slotSpin(mode, betCards, forceKind, forceCount);

            if (!res?.success) {
                if (res?.code === 'PENDING_WHEEL') {
                    setMessage('Spin the Slime Wheel first.');
                    await openWheel(res.pendingWheel?.source, res.pendingWheel?.wheelSpinId);
                    setBusy(false);
                    return;
                }
                if (res?.code === 'OUTSTANDING_SPIN' && res.outstanding) {
                    setMessage('Finish your unfinished round first.');
                    const o = res.outstanding;
                    if (o.spin?.grid) setGrid(o.spin.grid);
                    if (o.state === 'bonus' && o.bonus) {
                        setBonus({
                            phase: 'interrupted',
                            spinId: o.spinId,
                            bonusId: o.bonus.bonusId,
                            isSuper: o.bonus.isSuper,
                            totalSpins: o.bonus.totalSpins,
                        });
                    }
                } else {
                    setMessage(res?.message || 'Spin failed.');
                    if (res?.message) toast.error(res.message);
                }
                stopReelLoop();
                playLose();
                setBusy(false);
                return;
            }

            const s = res.spin;
            await new Promise(resolve => animateTo(s.grid, resolve));
            if (!mountedRef.current) return;

            const finalHot = highlightSet(s.lines);
            setHighlights(finalHot);
            setBannerWin(s.win);
            if (s.win > 0) setLastWin(s.win);
            setWaysWon((s.lines || []).reduce((n, l) => n + (l.ways || 0), 0));
            setWinTier(s.jackpotHit ? null : winTierFor(s.win, res.betCredits));
            const sfxTier = winSfxTier(s.win, res.betCredits);
            if (sfxTier >= 0 && !s.jackpotHit) {
                playWin(sfxTier);
                playCoins(2 + sfxTier * 3);
            } else if (teasedRef.current && !s.jackpotHit && !res.bonus && !res.wheel) {
                playWomp();
            }
            if (typeof res.balance === 'number') {
                setState(st => (st ? { ...st, stamps: { total: res.balance, cards: Math.floor(res.balance / 10), credits: res.balance * CREDITS_PER_STAMP } } : st));
            }

            const hasFeature = s.jackpotHit || !!res.bonus || !!res.wheel;
            const lineWin = (s.lines || []).length > 0 && s.win > 0;
            if (lineWin && hasFeature) {
                await new Promise(resolve => later(resolve, WIN_HOLD_MS));
                if (!mountedRef.current) return;
            }

            if (s.jackpotHit) {
                await flashTrigger('jackpot', s.jackpotPositions);
                if (!mountedRef.current) return;
                setJackpot({ amount: s.jackpotWin, mode });
            }

            if (res.bonus) {
                await flashTrigger('scatter', gridPositions(s.grid, 'scatter'));
                if (!mountedRef.current) return;
                startBonus(res.bonus);
            } else {
                if (!isGuest) await slotAck(res.spinId).catch(() => {});
                if (res.wheel) {
                    await flashTrigger('wheel', s.wheelPositions || gridPositions(s.grid, 'wheel'));
                    if (!mountedRef.current) return;
                    await openWheel(res.wheel.source, res.wheel.wheelSpinId);
                }
                await refreshState();
            }
        } catch (e) {
            stopReelLoop();
            setMessage('Could not reach the server.');
        } finally {
            if (mountedRef.current) setBusy(false);
        }
    }, [animateTo, betCards, betStamps, busy, flashTrigger, forceCount, forceKind, isGuest, later, mode, openWheel, refreshState, startBonus, usingGiftedSpin]);

    const handleResumeBonus = useCallback(async () => {
        if (!bonus?.bonusId) return;
        setBusy(true);
        setMessage('');
        try {
            await runBonusSteps(bonus.bonusId, bonus.isSuper, bonus.totalSpins);
        } finally {
            if (mountedRef.current) setBusy(false);
        }
    }, [bonus, runBonusSteps]);

    const handleStartStakes = useCallback(() => {
        primeAudio();
        playUi();
        setMode('stakes');
        setScreen('playing');
        setMessage('');
        setBonusSummary(null);
        setClaimResult(null);
    }, []);

    const handleStartDaily = useCallback(async () => {
        primeAudio();
        playUi();
        setBusy(true);
        try {
            const res = await startSlotDaily();
            if (!res?.success) { toast.error(res?.message || 'Could not start your free spins.'); return; }
            await refreshState();
            setMode('daily');
            setScreen('playing');
            setMessage('');
            setBonusSummary(null);
            setClaimResult(null);
        } finally {
            if (mountedRef.current) setBusy(false);
        }
    }, [refreshState]);

    const handleGoDaily = useCallback(async () => {
        if (daily?.state === 'active') {
            setMode('daily');
            setScreen('playing');
            setMessage('');
            return;
        }
        await handleStartDaily();
    }, [daily, handleStartDaily]);

    const handleClaim = useCallback(async () => {
        setBusy(true);
        try {
            const res = await claimSlotDaily();
            if (!res?.success) { toast.error(res?.message || 'Could not claim.'); return; }
            setClaimResult(res);
            setMode('stakes');
            toast.success(`${res.stampsAwarded} framp${res.stampsAwarded !== 1 ? 's' : ''} added to your account!`);
            await refreshState();
            await refreshBoards();
        } finally {
            if (mountedRef.current) setBusy(false);
        }
    }, [refreshBoards, refreshState]);

    const blocked = !!bonus || !!bonusIntro || !!jackpot;

    const canSpin = useMemo(() => {
        if (busy || blocked || machineClosed || payingNotice) return false;
        if (mode === 'daily') return !isGuest && daily?.state === 'active' && dailySpinsLeft > 0;
        if (isGuest) return true;
        return usingGiftedSpin || balance >= betStamps;
    }, [balance, betStamps, blocked, busy, daily, dailySpinsLeft, isGuest, machineClosed, mode, payingNotice, usingGiftedSpin]);

    const spinLabel = useMemo(() => {
        if (machineClosed) return 'Machine closed';
        if (payingNotice) return 'Confirm before spinning';
        if (blocked) return 'Bonus in progress';
        if (busy) return 'Spinning...';
        if (mode === 'daily') {
            if (isGuest) return 'Login to play';
            if (dailyDone) return 'Claimed today';
            if (dailySpinsLeft === 0) return 'Claim your framps';
            return `Free Spin (${dailySpinsLeft} left)`;
        }
        if (isGuest) return 'Demo Spin';
        if (usingGiftedSpin) return `Free Spin - ${freeSpinsLeft} left, costs you nothing`;
        if (balance < betStamps) return 'Not enough framps';
        return `Spin - ${cardsLabel(betCards)} (${fmt(betStamps)} framps)`;
    }, [balance, betCards, betStamps, blocked, busy, dailyDone, dailySpinsLeft, freeSpinsLeft, isGuest, machineClosed, mode, payingNotice, usingGiftedSpin]);

    const symbolRows = useMemo(
        () => (paytable ? paytable.symbols.filter(s => s.pays && SYMBOL_NAMES[s.id]) : []),
        [paytable]
    );

    const estimatedClaim = Math.min(
        economy?.dailyMaxStamps ?? 500,
        Math.round((daily?.totalWon || 0) / (economy?.dailyCreditsPerStamp || 8))
    );

    const renderReel = (r) => {
        const reel = reels[r] || { strip: grid[r] || [], offset: 0, transition: 'none' };
        const n = Math.max(ROWS, reel.strip.length);
        const spinning = spinningReels[r];
        const teasing = teaseReels[r];
        const justLanded = landedReel === r;

        return (
            <div
                key={r}
                style={{
                    position: 'relative',
                    overflow: 'hidden',
                    aspectRatio: `1 / ${ROWS}`,
                    borderRadius: 6,
                    background: teasing ? 'rgba(255,210,74,0.08)' : 'rgba(255,255,255,0.02)',
                    boxShadow: teasing
                        ? '0 0 20px rgba(255,210,74,0.4) inset'
                        : justLanded ? '0 0 14px rgba(255,255,255,0.10) inset' : 'none',
                    transition: 'background 0.2s ease, box-shadow 0.2s ease',
                    animation: teasing ? 'teaseGlow 0.45s ease-in-out infinite' : 'none',
                }}
            >
                <div
                    onTransitionEnd={(e) => {
                        if (e.target !== e.currentTarget) return;
                        if (e.propertyName !== 'transform') return;
                        if (reel.offset !== 0) return;
                        playReelStop(r);
                        if (!landedWheelsRef.current.has(r)
                            && reel.strip.slice(0, ROWS).includes('wheel')) {
                            landedWheelsRef.current.add(r);
                            playWheelLand(landedWheelsRef.current.size);
                        }
                    }}
                    style={{
                        display: 'grid',
                        gridTemplateRows: `repeat(${n}, 1fr)`,
                        height: `${(n / ROWS) * 100}%`,
                        transform: `translateY(-${(reel.offset / n) * 100}%)`,
                        transition: reel.transition,
                        willChange: 'transform',
                    }}
                >
                    {reel.strip.map((sym, idx) => {
                        const landedIdx = idx < ROWS && !spinning ? idx : -1;
                        const key = posKey(r, landedIdx);
                        const hot = landedIdx >= 0 && highlights.has(key);
                        const mult = landedIdx >= 0 ? wildMults[key] : null;
                        const isSpecial = (sym === 'scatter' || sym === 'jackpot') && !spinning && hot;
                        const dimmed = highlights.size > 0 && !hot;
                        // Only a landed cell can be judged out of the win. While a wild is
                        // still flying past nothing is decided yet, so it keeps its slime
                        // rather than flickering off and back on as the teaser greys out.
                        const excluded = landedIdx >= 0 && dimmed;
                        const isWild = sym === 'wild' && !excluded;
                        const wildDrips = isWild && !spinning;
                        const scatterSparks = sym === 'scatter' && !spinning && !excluded;
                        // Pumpkin orange: Halloween, and clear of the green wilds and
                        // maroon win ways. Escalates to pink when the bonus is live.
                        const isScatterLit = sym === 'scatter' && !excluded;
                        const isJackpotLit = sym === 'jackpot' && !excluded;
                        const isWheelLit = sym === 'wheel' && !excluded;
                        const flashing = !!flash && landedIdx >= 0 && flash.keys.has(key);

                        return (
                            <div
                                key={idx}
                                style={{
                                    position: 'relative',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    overflow: 'hidden',
                                    background: isSpecial
                                        ? (sym === 'jackpot' ? 'rgba(232,58,78,0.34)' : 'rgba(255,63,180,0.16)')
                                        : isWild
                                            ? 'radial-gradient(ellipse at 50% 15%, rgba(77,255,136,0.34) 0%, rgba(30,140,70,0.20) 55%, rgba(10,60,30,0.10) 100%)'
                                            : isScatterLit
                                            ? 'radial-gradient(ellipse at 50% 20%, rgba(255,140,32,0.34) 0%, rgba(190,88,14,0.22) 55%, rgba(80,34,4,0.12) 100%)'
                                            : isJackpotLit
                                            ? 'radial-gradient(ellipse at 50% 20%, rgba(176,36,54,0.40) 0%, rgba(112,20,34,0.26) 55%, rgba(48,8,14,0.14) 100%)'
                                            : isWheelLit
                                            ? 'radial-gradient(ellipse at 50% 20%, rgba(137,207,240,0.40) 0%, rgba(62,138,196,0.26) 55%, rgba(16,52,84,0.14) 100%)'
                                            : hot ? 'rgba(230,197,0,0.26)' : 'transparent',
                                    border: `1px solid ${
                                        isSpecial ? (sym === 'jackpot' ? 'rgba(255,92,112,0.9)' : 'rgba(255,63,180,0.55)')
                                        : isWild ? 'rgba(77,255,136,0.7)'
                                        : isScatterLit ? 'rgba(255,168,72,0.8)'
                                        : isJackpotLit ? 'rgba(214,58,78,0.85)'
                                        : isWheelLit ? 'rgba(168,222,255,0.95)'
                                        : hot ? 'rgba(255,220,60,0.95)'
                                        : 'rgba(137,207,240,0.42)'
                                    }`,
                                    borderRadius: 5,
                                    boxShadow: isWild
                                        ? 'inset 0 0 16px rgba(77,255,136,0.35)'
                                        : isScatterLit ? 'inset 0 0 16px rgba(255,140,32,0.36)'
                                        : isJackpotLit ? 'inset 0 0 16px rgba(200,44,62,0.42)'
                                        : isWheelLit ? 'inset 0 0 18px rgba(137,207,240,0.45)'
                                        : hot ? 'inset 0 0 18px rgba(230,197,0,0.35)' : 'none',
                                    animation: flashing
                                        ? `${flash.kind}FlashCell 0.34s ease-in-out infinite`
                                        : (retriggerFlash > 0 && sym === 'scatter' && !spinning)
                                        ? 'scatterFlash 0.42s ease-in-out infinite'
                                        : wildDrips ? 'slimeCell 2.4s ease-in-out infinite' : 'none',
                                    transition: 'background 0.18s ease, border-color 0.18s ease',
                                    filter: spinning ? (teasing ? 'blur(2.2px)' : 'blur(1.1px)') : 'none',
                                }}
                            >
                                {wildDrips && (
                                    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
                                        <div style={{
                                            position: 'absolute', top: -2, left: '-6%', right: '-6%', height: '26%',
                                            background: 'linear-gradient(180deg, rgba(93,231,133,0.85) 0%, rgba(59,190,101,0.55) 60%, rgba(59,190,101,0) 100%)',
                                            borderBottomLeftRadius: '52% 90%',
                                            borderBottomRightRadius: '44% 80%',
                                            filter: 'blur(0.4px)',
                                            animation: 'slimeLip 3.1s ease-in-out infinite',
                                        }} />
                                        {WILD_DRIPS.map((d, di) => (
                                            <span
                                                key={di}
                                                style={{
                                                    position: 'absolute',
                                                    left: d.left, top: '14%',
                                                    width: d.size, height: d.size,
                                                    borderRadius: '50% 50% 55% 55%',
                                                    background: 'radial-gradient(circle at 35% 28%, #d6ffe4 0%, #62f79a 45%, #1f9c4a 100%)',
                                                    boxShadow: '0 0 6px rgba(77,255,136,0.85)',
                                                    animation: `slimeDrip ${d.dur}s ease-in ${d.delay}s infinite`,
                                                }}
                                            />
                                        ))}
                                    </div>
                                )}
                                {scatterSparks && (
                                    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 3 }}>
                                        {SCATTER_SPARKS.map((k, ki) => (
                                            <span
                                                key={ki}
                                                style={{
                                                    position: 'absolute',
                                                    left: k.left, top: k.top,
                                                    width: k.size, height: k.size,
                                                    borderRadius: '50%',
                                                    background: 'radial-gradient(circle at 40% 35%, #f0ffe9 0%, #7dff9c 40%, #2fbf5c 100%)',
                                                    animation: `scatterSpark ${k.dur}s ease-out ${k.delay}s infinite`,
                                                    willChange: 'transform, opacity',
                                                }}
                                            />
                                        ))}
                                    </div>
                                )}
                                <SlotSymbol
                                    id={sym}
                                    size="74%"
                                    cellPx={cellPx}
                                    dim={dimmed}
                                />
                                {mult ? (
                                    <div style={{
                                        position: 'absolute', inset: 0,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        pointerEvents: 'none',
                                    }}>
                                        <span style={{
                                            color: '#eaffe9',
                                            fontSize: 'clamp(20px, 4.2vw, 40px)',
                                            lineHeight: 1,
                                            textShadow: '0 0 10px rgba(0,0,0,0.95), 0 2px 0 #0d2417, 0 0 22px rgba(77,255,136,0.9)',
                                        }}>
                                            x{mult}
                                        </span>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="mt-10">
            <div className="flex flex-col xl:flex-row gap-6 xl:items-start">

                <div
                    className="flex-1 min-w-0 tracking-widest"
                    onContextMenu={e => e.preventDefault()}
                    style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
                >
                    <ContentBox header="Slimeburger Spin!" theme="battleball" tint="#000F14" icon="/img/icons/icon_202.png">
                        <div
                            className="flex flex-col lg:flex-row rounded max-w-[1050px] mx-auto"
                            style={{
                                background: '#000F14',
                                border: '1px solid rgba(255,255,255,0.07)',
                                boxShadow: 'inset 0 0 60px rgba(0,0,0,0.5)',
                                fontFamily: "'Jersey 10', monospace",
                            }}
                        >
                            <div className="lg:w-52 flex-shrink-0 flex flex-col border-b lg:border-b-0 lg:border-r border-white/[0.06]">

                                <div className="flex lg:hidden flex-row items-center justify-between px-4 py-2 gap-4 border-b border-white/[0.06]">
                                    <div>
                                        <div className="text-white/30 text-xs uppercase tracking-[2px]">Cards</div>
                                        <div className="text-white text-lg leading-none tabular-nums">{isGuest ? '-' : fmt(balanceCards)}</div>
                                    </div>
                                    <div>
                                        <div className="text-white/30 text-xs uppercase tracking-[2px]">Last Win</div>
                                        <div className="text-lg leading-none tabular-nums" style={{ color: lastWin > 0 ? '#ffd84a' : 'white' }}>{fmt(lastWin)}</div>
                                    </div>
                                    <div>
                                        <div className="text-white/30 text-xs uppercase tracking-[2px]">Free</div>
                                        <div className="text-white text-lg leading-none tabular-nums">{isGuest ? '-' : dailySpinsLeft}</div>
                                    </div>
                                </div>

                                <div className="hidden lg:flex flex-col flex-1">
                                    <div className="px-4 py-4 border-b border-white/[0.06]">
                                        <div className="text-white/30 text-lg uppercase tracking-[3px] mb-1">Framp Cards</div>
                                        <div className="text-white text-[34px] leading-none tabular-nums" style={{ textShadow: '0 0 14px rgba(77,255,136,0.3)' }}>
                                            {isGuest ? '-' : fmt(balanceCards)}
                                        </div>
                                        <div className="text-white/40 text-base mt-1">
                                            {isGuest ? 'Demo mode' : `${fmt(balance)} framp${balance !== 1 ? 's' : ''}`}
                                        </div>
                                    </div>

                                    <div className="px-4 py-3 border-b border-white/[0.06]">
                                        <div className="text-white/30 text-lg uppercase tracking-[2px] mb-1">Last Win</div>
                                        <div className="text-[28px] leading-none tabular-nums" style={{ color: lastWin > 0 ? '#ffd84a' : 'rgba(255,255,255,0.5)' }}>
                                            {fmt(lastWin)} <span className="text-lg text-white/40">framps</span>
                                        </div>
                                        <div className="text-white/35 text-base" style={{ minHeight: '1.25em' }}>
                                            {lastWin >= 10 ? `${(lastWin / 10).toFixed(1)} framp cards` : '\u00a0'}
                                        </div>
                                    </div>

                                    <div className="px-4 py-3 border-b border-white/[0.06]">
                                        <div className="text-white/30 text-lg uppercase tracking-[2px] mb-1">Daily Free Spins</div>
                                        <div className="text-[24px] leading-none tabular-nums text-white">
                                            {isGuest ? 'Login' : dailyDone ? 'Claimed' : `${dailySpinsLeft} / ${daily?.spinsAllowed ?? 20}`}
                                        </div>
                                        {!isGuest && !dailyDone && (
                                        <div className="text-white/35 text-base mt-1" style={{ minHeight: '1.25em' }}>
                                            {daily?.state === 'active'
                                                ? `${fmt(daily.totalWon)} won · ~${fmt(estimatedClaim)} framps`
                                                : '\u00a0'}
                                        </div>
                                        )}
                                        {!isGuest && !dailyDone && (
                                        <div style={{ minHeight: 34, marginTop: 8 }}>
                                        {mode === 'stakes' && !blocked && !busy && (
                                            <button
                                                onClick={handleGoDaily}
                                                className="w-full py-1.5 rounded text-base uppercase tracking-[1px]"
                                                style={{
                                                    background: 'rgba(77,255,136,0.14)',
                                                    border: '1px solid rgba(77,255,136,0.35)',
                                                    color: '#8bffb0',
                                                }}
                                            >
                                                Play free spins
                                            </button>
                                        )}
                                        </div>
                                        )}
                                    </div>

                                    <div className="pl-4 py-3 flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <div className="text-white/30 text-lg uppercase tracking-[2px]">Top Wins</div>
                                            <div className="flex rounded overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                                                {[['alltime', 'All'], ['today', 'Today']].map(([p, label], idx) => (
                                                    <button
                                                        key={p}
                                                        onClick={() => setLbPeriod(p)}
                                                        className="px-2 py-0.5 text-sm uppercase tracking-[1px] transition-colors"
                                                        style={{
                                                            background: lbPeriod === p ? 'rgba(255,255,255,0.12)' : 'transparent',
                                                            color: lbPeriod === p ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.25)',
                                                            borderRight: idx === 0 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                                                        }}
                                                    >
                                                        {label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div style={{ overflowY: 'auto', height: 250 }}>
                                            {Array.from({ length: 20 }).map((_, i) => {
                                                const entry = leaderboard[i];
                                                return entry ? (
                                                    <div key={`${entry.habboName}-${i}`} className="py-1.5 border-b border-white/[0.04] last:border-0 mr-4">
                                                        <div className="flex items-baseline justify-between gap-2">
                                                            <span className="text-white/60 text-lg leading-tight" style={{ wordBreak: 'break-word' }}>
                                                                {i + 1}. {entry.habboName}
                                                            </span>
                                                            <span className="text-lg tabular-nums leading-tight flex-shrink-0" style={{ color: '#8bffb0' }}>
                                                                {fmtMult(entry.multiplier)}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-baseline justify-between gap-2">
                                                            <span className="text-base leading-none" style={{ color: entry.isSuper ? '#ff8ad6' : entry.fromBonus ? '#8bffb0' : 'rgba(255,255,255,0.25)' }}>
                                                                {entry.isSuper ? `Overload (${entry.mode === 'daily' ? 'F' : 'S'})` : (entry.label || 'Stakes')}
                                                            </span>
                                                            <span className="text-white/40 text-base tabular-nums leading-none flex-shrink-0">
                                                                {fmt(entry.stamps)} framps
                                                            </span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-white/[0.04] last:border-0 mr-4">
                                                        <span className="text-white/20 text-lg">{i + 1}. -</span>
                                                        <span className="text-white/15 text-lg">-</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div ref={areaRef} className="relative flex-1 flex flex-col min-h-[580px] sm:min-h-[500px] lg:min-h-[540px]">
                                <style>{`
                                    @keyframes slimePulse { 0%,100% { opacity: .35 } 50% { opacity: .9 } }
                                    @keyframes slimeRise { 0% { transform: translateY(8px); opacity: 0 } 100% { transform: translateY(0); opacity: 1 } }
                                    @keyframes teaseGlow { 0%,100% { box-shadow: 0 0 10px rgba(255,210,74,0.25) inset } 50% { box-shadow: 0 0 26px rgba(255,210,74,0.6) inset } }
                                    @keyframes bonusZoom { 0% { transform: scale(0.7); opacity: 0 } 60% { transform: scale(1.06) } 100% { transform: scale(1); opacity: 1 } }
                                    @keyframes winPop { 0% { transform: scale(0.75); opacity: 0 } 55% { transform: scale(1.08) } 100% { transform: scale(1); opacity: 1 } }
                                    @keyframes spinnerTurn { to { transform: rotate(360deg) } }
                                    @keyframes freeSpinPulse { 0%,100% { box-shadow: 0 0 12px rgba(109,243,94,0.18) } 50% { box-shadow: 0 0 24px rgba(109,243,94,0.45) } }
                                    @keyframes wheelFlashCell {
                                        0%, 100% { box-shadow: 0 0 10px rgba(137,207,240,0.55) inset; background: rgba(137,207,240,0.18); transform: scale(1) }
                                        50%      { box-shadow: 0 0 32px rgba(137,207,240,1) inset; background: rgba(137,207,240,0.55); transform: scale(1.06) }
                                    }
                                    @keyframes scatterFlashCell {
                                        0%, 100% { box-shadow: 0 0 10px rgba(255,140,32,0.55) inset; background: rgba(255,140,32,0.18); transform: scale(1) }
                                        50%      { box-shadow: 0 0 32px rgba(255,140,32,1) inset; background: rgba(255,140,32,0.55); transform: scale(1.06) }
                                    }
                                    @keyframes jackpotFlashCell {
                                        0%, 100% { box-shadow: 0 0 10px rgba(255,210,74,0.55) inset; background: rgba(214,58,78,0.24); transform: scale(1) }
                                        50%      { box-shadow: 0 0 34px rgba(255,210,74,1) inset; background: rgba(214,58,78,0.62); transform: scale(1.07) }
                                    }
                                    @keyframes slimeDrip {
                                        0%   { transform: translateY(0) scale(0.35); opacity: 0 }
                                        18%  { transform: translateY(60%) scale(1); opacity: 1 }
                                        78%  { transform: translateY(420%) scaleY(1.35) scaleX(0.85); opacity: 0.95 }
                                        100% { transform: translateY(560%) scaleY(0.7) scaleX(1.1); opacity: 0 }
                                    }
                                    @keyframes slimeLip {
                                        0%, 100% { transform: translateY(0) scaleY(1) }
                                        50%      { transform: translateY(6%) scaleY(1.18) }
                                    }
                                    @keyframes scatterSpark {
                                        0%   { transform: translateY(0) scale(0); opacity: 0 }
                                        7%   { transform: translateY(-20%) scale(1); opacity: 1 }
                                        26%  { transform: translateY(-190%) scale(0.65); opacity: 0.85 }
                                        44%  { transform: translateY(-330%) scale(0.15); opacity: 0 }
                                        100% { transform: translateY(-330%) scale(0); opacity: 0 }
                                    }
                                    @keyframes slimeCell {
                                        0%, 100% { box-shadow: inset 0 0 14px rgba(77,255,136,0.30) }
                                        50%      { box-shadow: inset 0 0 26px rgba(77,255,136,0.55) }
                                    }
                                    @keyframes scatterFlash {
                                        0%, 100% { box-shadow: 0 0 8px rgba(255,63,180,0.5) inset; background: rgba(255,63,180,0.14) }
                                        50% { box-shadow: 0 0 30px rgba(255,63,180,1) inset; background: rgba(255,63,180,0.5) }
                                    }
                                    @keyframes jackpotGlow { 0%,100% { text-shadow: 0 0 24px rgba(255,210,74,0.55) } 50% { text-shadow: 0 0 52px rgba(255,210,74,0.95) } }
                                    @keyframes jackpotSpin { 0% { transform: rotate(-9deg) scale(0.8); opacity: 0 } 60% { transform: rotate(5deg) scale(1.12) } 100% { transform: rotate(0deg) scale(1); opacity: 1 } }
                                    .slime-grid { gap: 3px; padding: 5px; }
                                    @media (min-width: 640px) { .slime-grid { gap: 4px; padding: 7px; } }
                                    .slime-payrow {
                                        display: flex; align-items: center; flex-wrap: wrap;
                                        gap: 6px 12px; padding: 7px 0;
                                        border-bottom: 1px solid rgba(255,255,255,0.06);
                                    }
                                    .slime-payrow-name { flex: 1 1 108px; min-width: 0; }
                                    .slime-pays {
                                        display: flex; gap: 10px; margin-left: auto;
                                        font-size: 17px; color: rgba(255,255,255,0.6);
                                    }
                                    .slime-pays > span { min-width: 44px; text-align: right; }
                                    @media (min-width: 420px) { .slime-pays > span { min-width: 60px; } }
                                    .slime-modal { padding: 14px 14px; border-radius: 12px; }
                                    @media (min-width: 640px) { .slime-modal { padding: 18px 20px; } }
                                `}</style>

                                <div style={{
                                    position: 'absolute', inset: 0,
                                    backgroundImage: `url(${ROOM_BG})`,
                                    backgroundRepeat: 'no-repeat',
                                    backgroundSize: `${ROOM_W * bgZoom}px ${ROOM_H * bgZoom}px`,
                                    backgroundPosition: `calc(50% + ${bgX}px) calc(50% + ${bgY}px)`,
                                    imageRendering: 'pixelated',
                                    pointerEvents: 'none',
                                }} />
                                <div style={{
                                    position: 'absolute', inset: 0,
                                    background: onTitle
                                        ? 'transparent'
                                        : 'radial-gradient(120% 90% at 50% 0%, rgba(77,255,136,0.08) 0%, rgba(11,10,22,0.72) 55%, rgba(11,10,22,0.9) 100%)',
                                    pointerEvents: 'none',
                                }} />

                                <div className="relative px-3 pt-3 pb-2 sm:pb-0 flex items-center justify-end gap-2" style={{ zIndex: 17 }}>
                                    <div className="flex items-center gap-2">
                                        <AudioSettings />
                                        {isAdmin && (
                                            <button
                                                onClick={() => setShowAdmin(true)}
                                                className="px-3 py-1 text-base uppercase tracking-[1px]"
                                                style={{
                                                    background: forceKind ? 'rgba(255,80,80,0.25)' : 'rgba(255,255,255,0.06)',
                                                    border: `1px solid ${forceKind ? 'rgba(255,80,80,0.6)' : 'rgba(255,255,255,0.1)'}`,
                                                    borderRadius: 4,
                                                    color: forceKind ? '#ff9b9b' : 'rgba(255,255,255,0.55)',
                                                }}
                                            >
                                                {forceKind ? `Admin: ${forceKind}` : 'Admin'}
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setShowPaytable(true)}
                                            className="px-3 py-1 text-base uppercase tracking-[1px]"
                                            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4, color: 'rgba(255,255,255,0.55)' }}
                                        >
                                            Paytable
                                        </button>
                                    </div>
                                </div>

                                {onTitle && <div className="relative flex-1" style={{ zIndex: 2 }} />}

                                {!onTitle && (
                                <div className="relative flex-1 flex items-center justify-center px-3 py-3" style={{ zIndex: 2 }}>
                                    <div className="slime-cab" style={{ width: '100%', maxWidth: 480 }}>
                                        {bonus?.phase === 'playing' && (
                                            <div style={{
                                                display: 'flex', justifyContent: 'center', marginBottom: 6,
                                            }}>
                                                <div style={{
                                                    background: 'rgba(6,16,10,0.85)',
                                                    border: `1px solid ${bonus.isSuper ? 'rgba(255,63,180,0.5)' : 'rgba(77,255,136,0.45)'}`,
                                                    borderRadius: 10, padding: '4px 18px', textAlign: 'center',
                                                }}>
                                                    <div style={{
                                                        color: bonus.isSuper ? '#ff8ad6' : '#8bffb0',
                                                        fontSize: 18, letterSpacing: '0.12em', textTransform: 'uppercase', lineHeight: 1.2,
                                                    }}>
                                                        {bonus.isSuper ? 'Slime Overload' : 'Free Spins'}
                                                        <span style={{ color: 'rgba(255,255,255,0.4)' }}>
                                                            {' '}· Spin {bonus.stepNumber || 0} of {bonus.totalSpins || '?'}
                                                        </span>
                                                    </div>
                                                    <div className="tabular-nums" style={{ color: bonus.isSuper ? '#ff8ad6' : '#8bffb0', fontSize: 28, lineHeight: 1.1 }}>
                                                        {fmt(bonus.running || 0)} <span style={{ fontSize: 18, color: 'rgba(255,255,255,0.6)' }}>framps</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {freeSpinBadge && !bonus && (
                                            <div style={{
                                                display: 'flex', justifyContent: 'center', marginBottom: 6,
                                                pointerEvents: 'none',
                                            }}>
                                                <div style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 8,
                                                    background: 'rgba(0,0,0,0.55)',
                                                    border: '1px solid rgba(109,243,94,0.5)',
                                                    borderRadius: 999, padding: '3px 14px',
                                                    color: '#6df35e', fontSize: 17,
                                                    textTransform: 'uppercase', letterSpacing: '0.14em',
                                                    boxShadow: '0 0 18px rgba(109,243,94,0.25)',
                                                    animation: 'freeSpinPulse 1.8s ease-in-out infinite',
                                                }}>
                                                    <span style={{
                                                        width: 7, height: 7, borderRadius: '50%',
                                                        background: '#6df35e', display: 'inline-block',
                                                    }} />
                                                    {freeSpinBadge.label} · {fmt(freeSpinBadge.left)} left
                                                </div>
                                            </div>
                                        )}
                                        <div ref={gridRef} className="slime-grid" style={{
                                            display: 'grid',
                                            gridTemplateColumns: `repeat(${REELS}, 1fr)`,
                                            background: 'rgba(0,0,0,0.42)',
                                            border: `1px solid ${bonus
                                                ? 'rgba(77,255,136,0.35)'
                                                : freeSpinBadge ? 'rgba(109,243,94,0.45)' : 'rgba(255,255,255,0.07)'}`,
                                            borderRadius: 10,
                                            transition: 'border-color 0.3s ease',
                                            boxShadow: freeSpinBadge && !bonus
                                                ? '0 8px 30px rgba(0,0,0,0.5), 0 0 22px rgba(109,243,94,0.18)'
                                                : '0 8px 30px rgba(0,0,0,0.5)',
                                        }}>
                                            {Array.from({ length: REELS }).map((_, r) => renderReel(r))}
                                        </div>

                                        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 mt-2 px-1 text-white/25 text-base uppercase tracking-[2px]">
                                            <span>{paytable ? `${fmt(paytable.ways)} Ways` : '4,096 Ways'}</span>
                                            <span className="text-white/15">·</span>
                                            {freeSpinBadge ? (
                                                <span style={{ color: '#6df35e' }}>
                                                    This spin is free
                                                    <span style={{ color: 'rgba(109,243,94,0.5)' }}> (no framp card taken)</span>
                                                </span>
                                            ) : (
                                                <span>
                                                    1 Spin = 1 Framp Card
                                                    <span className="text-white/20"> ({fmt(betStamps)} Framps)</span>
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {bannerWin > 0 && !retriggerFlash && (
                                        <div style={{
                                            position: 'absolute', top: '34%', left: 0, right: 0,
                                            display: 'flex', justifyContent: 'center',
                                            pointerEvents: 'none', animation: 'winPop 0.4s ease',
                                        }}>
                                            <div style={{
                                                background: 'rgba(6,5,14,0.88)',
                                                border: `2px solid ${winTier ? winTier.color : 'rgba(230,197,0,0.85)'}`,
                                                borderRadius: 12,
                                                padding: '10px 26px',
                                                textAlign: 'center',
                                                boxShadow: `0 8px 34px rgba(0,0,0,0.75), 0 0 26px ${(winTier ? winTier.color : '#e6c500')}44`,
                                                backdropFilter: 'blur(3px)',
                                            }}>
                                                {winTier && (
                                                    <div style={{
                                                        color: winTier.color, fontSize: 26, textTransform: 'uppercase',
                                                        letterSpacing: '0.1em', lineHeight: 1.1,
                                                    }}>
                                                        {winTier.label}
                                                    </div>
                                                )}
                                                <div style={{ color: 'white', fontSize: 34, lineHeight: 1.15 }} className="tabular-nums">
                                                    {fmt(bannerWin)} <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.75)' }}>framps</span>
                                                </div>
                                                {waysWon > 0 && (
                                                    <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 19, lineHeight: 1.2 }}>
                                                        {fmt(waysWon)} way{waysWon !== 1 ? 's' : ''}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {retriggerFlash > 0 && (
                                        <div style={{
                                            position: 'absolute', top: '42%', left: 0, right: 0, textAlign: 'center',
                                            color: '#ffd24a', fontSize: 34, textTransform: 'uppercase', letterSpacing: '0.1em',
                                            textShadow: '0 0 30px rgba(255,210,74,0.6)', pointerEvents: 'none',
                                            animation: 'bonusZoom 0.35s ease',
                                        }}>
                                            +{retriggerFlash} Free Spins!
                                        </div>
                                    )}
                                </div>

                                )}

                                {!onTitle && (
                                <div className="relative px-3 flex flex-col items-center gap-2" style={{ zIndex: 2, paddingBottom: 52 }}>
                                    {dailyClaimable && mode === 'daily' && !blocked ? (
                                        <button
                                            onClick={handleClaim}
                                            disabled={busy}
                                            className="w-full max-w-[320px] py-3 rounded text-[18px] sm:text-[22px] uppercase tracking-[2px] whitespace-nowrap transition-all hover:scale-[1.02] active:scale-95"
                                            style={{
                                                background: 'linear-gradient(135deg, #1d7a3a 0%, #3ddc6f 100%)',
                                                border: '1px solid rgba(255,255,255,0.12)', color: '#04140a',
                                                boxShadow: '0 4px 24px rgba(60,220,110,0.35)',
                                            }}
                                        >
                                            Claim {fmt(estimatedClaim)} Framps
                                        </button>
                                    ) : (
                                        <button
                                            onClick={handleSpin}
                                            disabled={!canSpin}
                                            title={spinLabel}
                                            aria-label={spinLabel}
                                            style={{
                                                width: 88, height: 88, borderRadius: '50%',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                background: canSpin
                                                    ? (freeSpinBadge
                                                        ? 'radial-gradient(circle at 50% 30%, #a6ff8e 0%, #35c93f 62%, #1d7a3a 100%)'
                                                        : 'radial-gradient(circle at 50% 30%, #ffd84a 0%, #e69700 62%, #a86a00 100%)')
                                                    : 'rgba(255,255,255,0.05)',
                                                border: `3px solid ${canSpin ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.12)'}`,
                                                boxShadow: canSpin
                                                    ? (freeSpinBadge
                                                        ? '0 6px 28px rgba(53,201,63,0.55), inset 0 2px 8px rgba(255,255,255,0.35)'
                                                        : '0 6px 28px rgba(230,151,0,0.5), inset 0 2px 8px rgba(255,255,255,0.35)')
                                                    : 'none',
                                                cursor: canSpin ? 'pointer' : 'not-allowed',
                                                transition: 'transform 0.12s ease, box-shadow 0.2s ease',
                                            }}
                                            onMouseDown={e => { if (canSpin) e.currentTarget.style.transform = 'scale(0.94)'; }}
                                            onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                                            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                                        >
                                            {busy ? (
                                                <svg width="46" height="46" viewBox="0 0 48 48" style={{ animation: 'spinnerTurn 0.9s linear infinite' }}>
                                                    <circle cx="24" cy="24" r="18" fill="none" stroke="rgba(74,34,0,0.28)" strokeWidth="5" />
                                                    <path d="M24 6a18 18 0 0 1 18 18" fill="none" stroke="#4a2200" strokeWidth="5" strokeLinecap="round" />
                                                </svg>
                                            ) : canSpin ? (
                                                <svg width="46" height="46" viewBox="0 0 48 48">
                                                    <path d="M40 24a16 16 0 1 1-4.7-11.3" fill="none"
                                                          stroke="#4a2200" strokeWidth="5" strokeLinecap="round" />
                                                    <path d="M39 5v10H29" fill="none" stroke="#4a2200" strokeWidth="5"
                                                          strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            ) : (
                                                <svg width="46" height="46" viewBox="0 0 48 48">
                                                    <circle cx="24" cy="24" r="17" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="5" />
                                                    <line x1="12" y1="36" x2="36" y2="12" stroke="rgba(255,255,255,0.3)" strokeWidth="5" strokeLinecap="round" />
                                                </svg>
                                            )}
                                        </button>
                                    )}
                                </div>

                                )}

                                {message && !onTitle && (
                                    <div className="relative px-3 pb-3 text-center text-lg" style={{ zIndex: 2, color: '#ffd24a' }}>
                                        {message}
                                    </div>
                                )}

                                {loading && (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ background: 'rgba(11,10,22,0.95)', zIndex: 20 }}>
                                        <div className="text-white/50 text-xl uppercase tracking-[4px] mb-5">Loading</div>
                                        <div className="flex gap-2">
                                            {SYMBOL_IDS.slice(0, 6).map((s, i) => (
                                                <div key={s} className="w-3 h-3 rounded-full"
                                                     style={{ background: SYMBOL_TINTS[s], animation: `slimePulse 1.2s ease-in-out ${i * 0.12}s infinite` }} />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {!loading && screen === 'title' && !blocked && (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center px-6 pt-14 pb-6 sm:py-6 text-center"
                                         style={{ background: 'rgba(8,7,18,0.86)', zIndex: 15 }}>
                                        <div className="text-lg text-white/25 uppercase tracking-[4px] -mb-2">Frankly Studios</div>
                                        <div className="text-white text-[44px] uppercase leading-none"
                                             style={{ textShadow: '0 0 40px rgba(77,255,136,0.4), 0 2px 0 rgba(0,0,0,0.8)' }}>
                                            Slimeburger Spin!
                                        </div>
                                        <div className="text-white/70 text-xl mt-2 leading-relaxed">
                                            <span className="text-white/50">{paytable ? fmt(paytable.ways) : '4,096'} ways · slime wilds · bonus &amp; super bonus</span>
                                        </div>

                                        <div className="flex gap-2 my-6">
                                            {SYMBOL_IDS.slice(0, 8).map(s => <SlotSymbol key={s} id={s} size={32} pixelScale={2} />)}
                                        </div>

                                        <div className="flex flex-col sm:flex-row gap-3">
                                            {!isGuest && !dailyDone && (
                                                <button
                                                    onClick={handleStartDaily}
                                                    disabled={busy}
                                                    className="px-8 py-3 rounded text-[20px] uppercase tracking-[2px] transition-all hover:scale-105 active:scale-95"
                                                    style={{
                                                        background: 'linear-gradient(135deg, #1d7a3a 0%, #3ddc6f 100%)',
                                                        color: '#04140a', border: '1px solid rgba(255,255,255,0.12)',
                                                        boxShadow: '0 4px 20px rgba(60,220,110,0.35)',
                                                    }}
                                                >
                                                    {daily?.state === 'active' ? `Resume Free Spins (${dailySpinsLeft})` : `Daily Free Spins (${dailySpinsLeft})`}
                                                </button>
                                            )}
                                            <button
                                                onClick={handleStartStakes}
                                                className="px-8 py-3 rounded text-[20px] uppercase tracking-[2px] transition-all hover:scale-105 active:scale-95"
                                                style={{
                                                    background: 'linear-gradient(135deg, #6b1d8a 0%, #c02890 100%)',
                                                    color: 'white', border: '1px solid rgba(255,255,255,0.1)',
                                                    boxShadow: '0 4px 20px rgba(180,40,140,0.3)',
                                                }}
                                            >
                                                {isGuest ? 'Try the Demo' : 'Framp Stakes'}
                                            </button>
                                        </div>

                                        <div className="text-white/30 text-base uppercase tracking-[2px] mt-4 max-w-md">
                                            {isGuest
                                                ? 'Not logged in - Framp Stakes runs as a free demo. No framps are won or lost.'
                                                : dailyDone
                                                    ? <>Free spins done for today.<br />Framp Stakes costs real framp cards.</>
                                                    : 'Take your free spins first, or skip straight to Framp Stakes - that one costs real framp cards.'}
                                        </div>
                                    </div>
                                )}

                                {bonusIntro && (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center"
                                         style={{
                                             background: bonusIntro.isSuper ? 'rgba(26,4,20,0.93)' : 'rgba(6,16,10,0.92)',
                                             backdropFilter: 'blur(4px)', zIndex: 16,
                                         }}>
                                        <div style={{ animation: 'bonusZoom 0.45s ease' }}>
                                            <div className="text-[40px] uppercase leading-none mb-2"
                                                 style={{
                                                     color: bonusIntro.isSuper ? '#ff3fb4' : '#8bffb0',
                                                     textShadow: `0 0 40px ${bonusIntro.isSuper ? 'rgba(255,63,180,0.6)' : 'rgba(77,255,136,0.55)'}`,
                                                 }}>
                                                {bonusIntro.isSuper ? 'Slime Overload!' : 'Bonus!'}
                                            </div>
                                            <div className="text-white text-[30px] uppercase leading-none">
                                                {bonusIntro.spins} Free Spins
                                            </div>
                                            <div className="text-white/50 text-xl mt-3">
                                                {bonusIntro.scatterCount} Free Spins symbols
                                            </div>
                                            <div className="text-white/40 text-lg mt-1 mb-6">
                                                {bonusIntro.isSuper
                                                    ? 'A full reel turns wild every spin - big slime multipliers'
                                                    : 'Wild multipliers are live'}
                                            </div>
                                            <button
                                                onClick={confirmBonus}
                                                className="px-10 py-3 rounded text-[22px] uppercase tracking-[2px] transition-all hover:scale-105 active:scale-95"
                                                style={{
                                                    background: bonusIntro.isSuper
                                                        ? 'linear-gradient(135deg, #8a1060 0%, #ff3fb4 100%)'
                                                        : 'linear-gradient(135deg, #1d7a3a 0%, #3ddc6f 100%)',
                                                    color: bonusIntro.isSuper ? 'white' : '#04140a',
                                                    border: '1px solid rgba(255,255,255,0.2)',
                                                    boxShadow: '0 6px 26px rgba(0,0,0,0.5)',
                                                }}
                                            >
                                                Start Bonus
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {bonus?.phase === 'interrupted' && (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center"
                                         style={{ background: 'rgba(6,16,10,0.92)', zIndex: 16 }}>
                                        <div className="text-[28px] uppercase mb-2" style={{ color: '#ffd24a' }}>Bonus Paused</div>
                                        <div className="text-white/60 text-xl mb-5 max-w-sm">
                                            Your bonus round is saved on the server. Resume to reveal the rest - you cannot spin again until it is finished.
                                        </div>
                                        <button
                                            onClick={handleResumeBonus}
                                            disabled={busy}
                                            className="px-8 py-3 rounded text-[20px] uppercase tracking-[2px]"
                                            style={{ background: 'linear-gradient(135deg, #1d7a3a 0%, #3ddc6f 100%)', color: '#04140a', border: '1px solid rgba(255,255,255,0.12)' }}
                                        >
                                            Resume Bonus
                                        </button>
                                    </div>
                                )}

                                {bonusSummary && (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center"
                                         style={{ background: 'rgba(6,16,10,0.9)', backdropFilter: 'blur(4px)', zIndex: 16 }}>
                                        <div className="text-[30px] uppercase mb-1" style={{ color: bonusSummary.isSuper ? '#ff3fb4' : '#8bffb0' }}>
                                            {bonusSummary.isSuper ? 'Slime Overload Complete' : 'Bonus Complete'}
                                        </div>
                                        <div className="text-white/40 text-xl">Total bonus win</div>
                                        <div className="text-white text-[46px] tabular-nums leading-none mt-1" style={{ animation: 'slimeRise 0.4s ease' }}>
                                            {fmt(bonusSummary.total)} <span className="text-[26px] text-white/70">framps</span>
                                        </div>
                                        <div className="text-white/45 text-xl mb-6">
                                            {(bonusSummary.total / 10).toFixed(1)} framp cards
                                        </div>
                                        <button
                                            onClick={() => setBonusSummary(null)}
                                            className="px-8 py-2.5 rounded text-[20px] uppercase tracking-[2px]"
                                            style={{ background: 'linear-gradient(135deg, #6b1d8a 0%, #c02890 100%)', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }}
                                        >
                                            Continue
                                        </button>
                                    </div>
                                )}

                                {jackpot && (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center"
                                         style={{
                                             background: 'radial-gradient(circle at 50% 45%, rgba(90,60,0,0.96) 0%, rgba(12,9,3,0.97) 70%)',
                                             backdropFilter: 'blur(5px)', zIndex: 30,
                                         }}>
                                        <div style={{ animation: 'jackpotSpin 0.6s ease' }}>
                                            <SlotSymbol id="jackpot" size={72} />
                                        </div>
                                        <div className="text-[44px] uppercase leading-none mt-3"
                                             style={{ color: '#ffd24a', animation: 'jackpotGlow 1.3s ease-in-out infinite' }}>
                                            Jackpot!
                                        </div>
                                        <div className="text-white/60 text-xl mt-2">
                                            Six Framp Stamps, one on every reel
                                        </div>
                                        <div className="text-white text-[40px] tabular-nums mt-3">
                                            {fmt(jackpot.amount)} framps
                                        </div>
                                        <div className="text-white/40 text-lg mb-6">
                                            {Math.floor(jackpot.amount / 10)} full framp cards
                                            {jackpot.mode === 'daily' ? ' - added to your daily total' : ' - added to your balance'}
                                        </div>
                                        <button
                                            onClick={() => setJackpot(null)}
                                            className="px-10 py-3 rounded text-[22px] uppercase tracking-[2px] transition-all hover:scale-105 active:scale-95"
                                            style={{
                                                background: 'linear-gradient(135deg, #a06a00 0%, #ffd24a 100%)',
                                                color: '#231800', border: '1px solid rgba(255,255,255,0.25)',
                                                boxShadow: '0 6px 30px rgba(255,210,74,0.45)',
                                            }}
                                        >
                                            Collect
                                        </button>
                                    </div>
                                )}

                                {claimResult && (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center"
                                         style={{ background: 'rgba(6,16,10,0.92)', backdropFilter: 'blur(4px)', zIndex: 17 }}>
                                        <div className="text-[30px] uppercase mb-1" style={{ color: '#8bffb0' }}>Daily Spins Claimed</div>
                                        <div className="text-white/40 text-xl">{fmt(claimResult.creditsWon)} credits won</div>
                                        <div className="text-white text-[40px] tabular-nums">{fmt(claimResult.stampsAwarded)} framps</div>
                                        <div className="text-white/40 text-lg mb-2">{Math.floor(claimResult.stampsAwarded / 10)} full framp cards</div>
                                        {claimResult.jackpots > 0 && (
                                            <div className="text-lg mb-2" style={{ color: '#ffd24a' }}>
                                                Including {claimResult.jackpots > 1 ? `${claimResult.jackpots} jackpots` : 'a JACKPOT'}!
                                            </div>
                                        )}
                                        <div className="text-white/30 text-base uppercase tracking-[2px] mb-6 max-w-sm">
                                            Framp Stakes is now unlocked - from here every spin costs real framp cards
                                        </div>
                                        <button
                                            onClick={() => { setClaimResult(null); setMode('stakes'); setScreen('playing'); }}
                                            className="px-8 py-2.5 rounded text-[20px] uppercase tracking-[2px]"
                                            style={{ background: 'linear-gradient(135deg, #6b1d8a 0%, #c02890 100%)', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }}
                                        >
                                            Continue
                                        </button>
                                    </div>
                                )}

                                {payingNotice && (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center"
                                         style={{ background: 'rgba(14,10,2,0.93)', backdropFilter: 'blur(5px)', zIndex: 35 }}>
                                        <div className="text-[34px] uppercase leading-none" style={{ color: '#ffd24a' }}>
                                            Free spins used up
                                        </div>
                                        <div className="text-white/70 text-xl mt-3 max-w-sm leading-relaxed">
                                            From here every spin costs a framp card
                                            <span className="text-white/40"> ({fmt(betStamps)} framps)</span>.
                                        </div>
                                        <div className="text-white/35 text-lg mt-1 mb-6">
                                            You have {fmt(balanceCards)} full framp card{balanceCards !== 1 ? 's' : ''}.
                                        </div>
                                        <button
                                            onClick={() => setPayingNotice(false)}
                                            className="px-9 py-3 rounded text-[21px] uppercase tracking-[2px] transition-all hover:scale-105 active:scale-95"
                                            style={{
                                                background: 'linear-gradient(135deg, #a06a00 0%, #ffd24a 100%)',
                                                color: '#231800', border: '1px solid rgba(255,255,255,0.25)',
                                                boxShadow: '0 6px 30px rgba(255,210,74,0.4)',
                                            }}
                                        >
                                            I understand
                                        </button>
                                    </div>
                                )}

                                {giftPrompt && (
                                    <div
                                        onClick={() => setGiftPrompt(null)}
                                        style={{
                                            position: 'absolute', inset: 0, zIndex: 40,
                                            background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(6px)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12,
                                            fontFamily: "'Jersey 10', monospace",
                                        }}
                                    >
                                        <div
                                            onClick={e => e.stopPropagation()}
                                            style={{
                                                background: '#0d0b1a', border: '1px solid rgba(109,243,94,0.4)', borderRadius: 12,
                                                padding: '20px 22px', maxWidth: 400, width: '100%', color: 'white', textAlign: 'center',
                                                maxHeight: '100%', overflowY: 'auto',
                                            }}
                                        >
                                            <div style={{ fontSize: 46, color: '#6df35e', lineHeight: 1.1, marginBottom: 8 }}>
                                                {giftPrompt.freeSpins > 0
                                                    ? `You have ${giftPrompt.freeSpins} free spin${giftPrompt.freeSpins !== 1 ? 's' : ''}!`
                                                    : 'You have a free bonus round!'}
                                            </div>
                                            <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 18, marginBottom: 20 }}>
                                                {giftPrompt.freeSpins > 0
                                                    ? 'They are used automatically before any framp cards are taken, and they pay out exactly the same.'
                                                    : 'It will trigger on your next spin.'}
                                                {giftPrompt.bonusRounds > 0 && giftPrompt.freeSpins > 0
                                                    ? ` You also have ${giftPrompt.bonusRounds} free bonus round${giftPrompt.bonusRounds !== 1 ? 's' : ''} waiting.`
                                                    : ''}
                                            </div>
                                            {giftPrompt.notes && giftPrompt.notes.length > 0 && (
                                                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 17, marginBottom: 20, fontStyle: 'italic' }}>
                                                    {giftPrompt.notes[0]}
                                                </div>
                                            )}
                                            <button
                                                onClick={() => setGiftPrompt(null)}
                                                style={{
                                                    padding: '10px 26px', borderRadius: 8, fontSize: 22,
                                                    background: 'rgba(109,243,94,0.18)', border: '1px solid rgba(109,243,94,0.5)',
                                                    color: '#6df35e', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.08em',
                                                }}
                                            >
                                                Bring them on
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {wheel && (
                                    <div
                                        className="absolute inset-0 flex flex-col items-center justify-center px-3"
                                        style={{
                                            background: 'radial-gradient(circle at 50% 45%, rgba(40,6,32,0.9) 0%, rgba(8,5,14,0.95) 70%)',
                                            backdropFilter: 'blur(4px)', zIndex: 45,
                                        }}
                                    >
                                        <div style={{ animation: 'bonusZoom 0.45s ease' }}>
                                            <PrizeWheel
                                                segments={wheelSegments}
                                                landedIndex={wheelResult ? wheelResult.landedIndex : null}
                                                spinning={wheelSpinning}
                                                size={wheelSize}
                                                onSettled={handleWheelSettled}
                                                onTick={playWheelTick}
                                            />
                                        </div>

                                        <div style={{ minHeight: 92, marginTop: 12, textAlign: 'center', width: '100%', maxWidth: 360 }}>
                                            {!wheelRevealed && (
                                                <button
                                                    onClick={handleSpinWheel}
                                                    disabled={wheelSpinning || !!wheelResult}
                                                    className="px-8 py-2.5 rounded uppercase tracking-[2px] transition-all hover:scale-105 active:scale-95"
                                                    style={{
                                                        fontSize: 22,
                                                        background: wheelSpinning
                                                            ? 'rgba(255,255,255,0.06)'
                                                            : 'linear-gradient(135deg, #8a1d6a 0%, #ff5fd2 100%)',
                                                        border: '1px solid rgba(255,255,255,0.25)',
                                                        color: wheelSpinning ? 'rgba(255,255,255,0.4)' : '#1a0512',
                                                        cursor: wheelSpinning ? 'default' : 'pointer',
                                                        boxShadow: wheelSpinning ? 'none' : '0 6px 26px rgba(255,95,210,0.45)',
                                                    }}
                                                >
                                                    {wheelSpinning ? 'Spinning...' : 'Spin'}
                                                </button>
                                            )}

                                            {wheelRevealed && wheelResult && (
                                                <div style={{ animation: 'winPop 0.35s ease' }}>
                                                    <div style={{ color: '#ff5fd2', fontSize: 32, lineHeight: 1.1 }}>
                                                        {wheelResult.label}
                                                    </div>
                                                    <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 18, lineHeight: 1.25 }}>
                                                        {wheelResult.outcome === 'item' && 'Added to your account'}
                                                        {wheelResult.outcome === 'delivery' && (wheelResult.quantity > 1
                                                            ? `x${wheelResult.quantity} - staff will deliver this in the hotel`
                                                            : 'Staff will deliver this to you in the hotel')}
                                                        {wheelResult.outcome === 'framps' && `+${fmt(wheelResult.framps)} framps`}
                                                        {wheelResult.outcome === 'duplicate' && `Already owned - +${fmt(wheelResult.framps)} framps instead`}
                                                        {wheelResult.outcome === 'nothing' && (wheelResult.framps > 0
                                                            ? `+${fmt(wheelResult.framps)} framps`
                                                            : 'Nothing this time')}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {showAdmin && isAdmin && (
                                    <div
                                        onClick={() => setShowAdmin(false)}
                                        style={{
                                            position: 'absolute', inset: 0, zIndex: 40,
                                            background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12,
                                            fontFamily: "'Jersey 10', monospace",
                                        }}
                                    >
                                        <div
                                            onClick={e => e.stopPropagation()}
                                            style={{
                                                background: '#150b0b', border: '1px solid rgba(255,80,80,0.35)', borderRadius: 12,
                                                padding: '18px 20px', maxWidth: 460, width: '100%', color: 'white',
                                                maxHeight: '100%', overflowY: 'auto',
                                            }}
                                        >
                                            <div style={{ color: '#ff9b9b', letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: 4 }}>
                                                Admin - forced outcomes
                                            </div>
                                            <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 17, marginBottom: 16 }}>
                                                The next spin will be forced to this result. Everything else behaves normally -
                                                the bet is taken and the win is paid for real. Clear it when you are done.
                                            </div>

                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                                                {forceKinds.map(k => (
                                                    <button
                                                        key={k}
                                                        onClick={() => setForceKind(forceKind === k ? null : k)}
                                                        style={{
                                                            padding: '8px 14px', borderRadius: 6, fontSize: 18,
                                                            textTransform: 'uppercase', letterSpacing: '0.06em',
                                                            background: forceKind === k ? 'rgba(255,80,80,0.3)' : 'rgba(255,255,255,0.06)',
                                                            border: `1px solid ${forceKind === k ? 'rgba(255,80,80,0.7)' : 'rgba(255,255,255,0.12)'}`,
                                                            color: forceKind === k ? '#ffd0d0' : 'rgba(255,255,255,0.7)',
                                                            cursor: 'pointer', fontFamily: 'inherit',
                                                        }}
                                                    >
                                                        {k}
                                                    </button>
                                                ))}
                                            </div>

                                            {forceKind === 'scatters' && (
                                                <div style={{ marginBottom: 14 }}>
                                                    <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 17, marginBottom: 6 }}>
                                                        Bonus symbols on the board
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 6 }}>
                                                        {[2, 3, 4, 5, 6].map(n => (
                                                            <button
                                                                key={n}
                                                                onClick={() => setForceCount(n)}
                                                                style={{
                                                                    flex: 1, padding: '8px 0', borderRadius: 6, fontSize: 19,
                                                                    background: forceCount === n ? 'rgba(77,255,136,0.22)' : 'rgba(255,255,255,0.06)',
                                                                    border: `1px solid ${forceCount === n ? 'rgba(77,255,136,0.6)' : 'rgba(255,255,255,0.12)'}`,
                                                                    color: 'white', cursor: 'pointer', fontFamily: 'inherit',
                                                                }}
                                                            >
                                                                {n}
                                                            </button>
                                                        ))}
                                                    </div>
                                                    <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 16, marginTop: 6 }}>
                                                        2 = no bonus (retrigger value only) · 3 = 15 spins · 4 = 20 · 5 = 50 · 6 = Slime Overload
                                                    </div>
                                                </div>
                                            )}

                                            <div style={{ display: 'flex', gap: 10 }}>
                                                <button
                                                    onClick={() => { setForceKind(null); setShowAdmin(false); }}
                                                    style={{
                                                        flex: 1, padding: '10px 0', borderRadius: 6, fontSize: 18,
                                                        background: 'transparent', border: '1px solid rgba(255,255,255,0.15)',
                                                        color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase',
                                                        cursor: 'pointer', fontFamily: 'inherit',
                                                    }}
                                                >
                                                    Clear
                                                </button>
                                                <button
                                                    onClick={() => setShowAdmin(false)}
                                                    style={{
                                                        flex: 1, padding: '10px 0', borderRadius: 6, fontSize: 18,
                                                        background: 'linear-gradient(135deg, #8a1a1a 0%, #d03a3a 100%)',
                                                        border: '1px solid rgba(255,255,255,0.15)', color: 'white',
                                                        textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit',
                                                    }}
                                                >
                                                    Done
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {showPaytable && (
                                    <div
                                        onClick={() => setShowPaytable(false)}
                                        style={{
                                            position: 'absolute', inset: 0, zIndex: 40,
                                            background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8,
                                            fontFamily: "'Jersey 10', monospace",
                                        }}
                                    >
                                        <div
                                            onClick={e => e.stopPropagation()}
                                            className="slime-modal"
                                            style={{
                                                background: '#0d0b1a', border: '1px solid rgba(255,255,255,0.1)',
                                                maxWidth: 560, width: '100%', maxHeight: '100%', overflowY: 'auto',
                                                WebkitOverflowScrolling: 'touch',
                                                color: 'white',
                                            }}
                                        >
                                            <div style={{ color: 'rgba(255,255,255,0.35)', letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: 4 }}>
                                                Paytable · per way · multiple of your bet
                                            </div>
                                            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 17, marginBottom: 14 }}>
                                                1 credit = 1 framp, and 10 framps is a full card. Every symbol pays from three
                                                in a row on consecutive reels from the left, and the smallest win is always 1 framp.
                                            </div>

                                            {symbolRows.map(s => (
                                                <div key={s.id} className="slime-payrow">
                                                    <SlotSymbol id={s.id} size={34} />
                                                    <div className="slime-payrow-name">
                                                        <div style={{ fontSize: 18, color: SYMBOL_TINTS[s.id] }}>{SYMBOL_NAMES[s.id] || s.name}</div>
                                                    </div>
                                                    <div className="slime-pays">
                                                        {[3, 4, 5, 6].map(n => (
                                                            <span key={n} className="tabular-nums">
                                                                <span style={{ color: 'rgba(255,255,255,0.25)' }}>{n}× </span>
                                                                {s.pays[n] != null
                                                                    ? s.pays[n]
                                                                    : <span style={{ color: 'rgba(255,255,255,0.15)' }}>-</span>}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}

                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0 7px' }}>
                                                <SlotSymbol id="wild" size={34} />
                                                <div style={{ fontSize: 17, color: 'rgba(255,255,255,0.6)' }}>
                                                    <span style={{ color: SYMBOL_TINTS.wild, fontSize: 18 }}>Wild</span> - substitutes for everything except the Free Spins symbol and the Framp Stamp. Carries x2 / x3 / x5 / x10, which only counts during a bonus. Multipliers on screen are added together.
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '7px 0' }}>
                                                <SlotSymbol id="scatter" size={34} />
                                                <div style={{ fontSize: 17, color: 'rgba(255,255,255,0.6)' }}>
                                                    <span style={{ color: SYMBOL_TINTS.scatter, fontSize: 18 }}>Free Spins</span> - 3 = 15 spins, 4 = 20 spins, 5 = 50 spins, 6 = Slime Overload. Also pays{' '}
                                                    {paytable ? Object.entries(paytable.scatterPay).map(([k, v]) => `${k}× = ${v}`).join(', ') : ''}.
                                                </div>
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '7px 0' }}>
                                                <SlotSymbol id="jackpot" size={34} />
                                                <div style={{ fontSize: 17, color: 'rgba(255,255,255,0.6)' }}>
                                                    <span style={{ color: SYMBOL_TINTS.jackpot, fontSize: 18 }}>Framp Stamp</span> - only ever one per reel. Pays nothing at all unless you land one on
                                                    every single reel, which instantly wins {fmt(paytable?.jackpotStamps ?? 5000)} framps
                                                    regardless of your bet. Roughly a 1 in 100,000 spin.
                                                </div>
                                            </div>
                                            <div style={{
                                                marginTop: 16, padding: '10px 14px', borderRadius: 8,
                                                background: 'rgba(255,210,74,0.10)', border: '1px solid rgba(255,210,74,0.35)',
                                                fontSize: 18, color: '#ffd88a',
                                            }}>
                                                <div>
                                                    Maximum payout is {fmt(paytable?.maxWinFramps ?? 5000)} framps, and that is the
                                                    ceiling for a whole round - one spin, or an entire bonus or Slime Overload added
                                                    together - no matter how big your bet or how many ways you hit.
                                                </div>
                                                <div style={{ marginTop: 8 }}>
                                                    The Framp jackpot pays its full {fmt(paytable?.jackpotStamps ?? 5000)} framps on top of that.
                                                </div>
                                            </div>

                                            <button
                                                onClick={() => setShowPaytable(false)}
                                                style={{
                                                    marginTop: 18, width: '100%', padding: '10px 0', borderRadius: 6,
                                                    background: 'linear-gradient(135deg, #6b1d8a 0%, #c02890 100%)',
                                                    border: '1px solid rgba(255,255,255,0.1)', color: 'white',
                                                    letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'inherit', fontSize: 18,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                Close
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {!loading && machineClosed && (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center"
                                         style={{ background: 'rgba(8,7,18,0.94)', backdropFilter: 'blur(4px)', zIndex: 60 }}>
                                        <div className="text-[38px] uppercase leading-none" style={{ color: '#ffd24a' }}>
                                            Closed
                                        </div>
                                        <div className="text-white/60 text-xl mt-3 max-w-sm leading-relaxed">
                                            {state?.closedMessage || 'Slimeburger Spin is having a clean down. Back shortly.'}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </ContentBox>

                    <div className="flex flex-col sm:flex-row gap-5">
                        <div className="flex-1">
                            <ContentBox header="How to Play" theme="battleball" tint="green">
                                <div className="space-y-5 p-1">
                                    <div>
                                        <div className="text-coal/50 text-sm uppercase tracking-[2px] mb-1">The Reels</div>
                                        <div className="text-coal text-sm">
                                            Six reels, four rows, {paytable ? fmt(paytable.ways) : '4,096'} ways to win. There are no paylines - match three or more of the same symbol on consecutive reels starting from the left and you are paid for every way it appears.
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-coal/50 text-sm uppercase tracking-[2px] mb-1">Slime Blob Wild</div>
                                        <div className="text-coal text-sm">
                                            The Wild substitutes for every symbol except the Free Spins symbol and the Framp Stamp. It carries a random x2, x3, x5 or x10 multiplier, but that multiplier only pays during a bonus round - in the base game it is a plain wild. Every multiplier on screen is added together and applied to that spin.
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-coal/50 text-sm uppercase tracking-[2px] mb-1">Bonus</div>
                                        <div className="text-coal text-sm leading-relaxed">
                                            Land Free Spins symbols anywhere to trigger the bonus:
                                            <div className="mt-1.5 space-y-1">
                                                <div className="flex justify-between border-b border-black/10 pb-1"><span>3 symbols</span><span className="text-coal/60">15 free spins</span></div>
                                                <div className="flex justify-between border-b border-black/10 pb-1"><span>4 symbols</span><span className="text-coal/60">20 free spins</span></div>
                                                <div className="flex justify-between border-b border-black/10 pb-1"><span>5 symbols</span><span className="text-coal/60">50 free spins</span></div>
                                                <div className="flex justify-between"><span>6 symbols</span><span className="text-coal/60">Slime Overload</span></div>
                                            </div>
                                            <div className="mt-2">Landing more of them during the bonus retriggers it for extra spins.</div>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-coal/50 text-sm uppercase tracking-[2px] mb-1">Super Bonus</div>
                                        <div className="text-coal text-sm">
                                            Six Free Spins symbols at once floods the restaurant - Slime Overload. {paytable?.superRound?.spins ?? 18} free spins with richer wild multipliers than the normal bonus, and a {Math.round((paytable?.superRound?.reelWildChance ?? 0.25) * 100)}% chance on each spin that an entire reel turns wild. Any bonus round can also erupt into one at random. Roughly a 1 in {fmt(paytable?.superOdds ?? 127000)} spin, so do not hold your breath.
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-coal/50 text-sm uppercase tracking-[2px] mb-1">Maximum Payout</div>
                                        <div className="text-coal text-sm">
                                            No round can pay more than <b>{fmt(paytable?.maxWinFramps ?? 5000)} framps</b> ({(((paytable?.maxWinFramps ?? 5000)) / 10).toFixed(0)} framp cards), whatever your bet and however many ways you land. That ceiling covers a whole bonus round too, so a long run of free spins or a Slime Overload cannot stack past it.
                                        </div>
                                        <div className="text-coal text-sm mt-2">
                                            The Framp jackpot is separate and always pays in full.
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-coal/50 text-sm uppercase tracking-[2px] mb-1">Slime Drop</div>
                                        <div className="text-coal text-sm">
                                            About once every 90 spins the ceiling gives way and two or three whole reels turn wild before the win is worked out.
                                        </div>
                                    </div>
                                </div>
                            </ContentBox>
                        </div>

                        <div className="flex-1 min-w-0">
                            <ContentBox header="Modes &amp; Framps" theme="battleball" tint="maroon">
                                <div className="p-1 space-y-4">
                                    <div>
                                        <b className="text-coal/75 text-sm uppercase tracking-[2px]">Daily Free Spins</b>
                                        <div className="text-coal text-sm mt-1">
                                            Every day you get {economy?.dailySpins ?? 20} free spins at the lowest bet. They cost you nothing, so the free spin reels carry extra Slime Splats and pay out far less often than Framp Stakes - expect plenty of dead spins, the odd small win, and now and then a very big one. Every credit you win becomes 1 framp - 10 framps is a full framp card. Only Daily Free Spins count towards the leaderboard and the score submissions.
                                        </div>
                                    </div>
                                    <div>
                                        <b className="text-coal/75 text-sm uppercase tracking-[2px]">Framp Stakes</b>
                                        <div className="text-coal text-sm mt-1">
                                            <b>This mode costs you real framp cards.</b> Every spin costs exactly 1 framp card - that is 10 framps. Credits and framps are the same thing here, so a win of 10 credits is 10 framps, a full framp card. Nothing here is submitted to the leaderboard. If you are not logged in you can still play it as a demo - no framps are won or lost.
                                        </div>
                                    </div>
                                    <div>
                                        <b className="text-coal/75 text-sm uppercase tracking-[2px]">Fair Play</b>
                                        <div className="text-coal text-sm mt-1">
                                            Every reel stop is generated on the server and written to the database before your browser sees it. An unfinished spin or bonus round has to be completed before you can spin again, and if you close the tab mid-bonus it will be waiting for you when you come back.
                                        </div>
                                    </div>
                                </div>
                            </ContentBox>
                        </div>
                    </div>
                </div>

                <div className="w-full xl:w-72 flex-shrink-0">
                    <ContentBox header="Latest Score Submissions" theme="battleball" tint="#cfcfcf">
                        <div className="p-1 min-h-[640px] max-h-[640px] overflow-y-auto">
                            {submissions.length === 0 ? (
                                <div className="text-coal/30 text-sm text-center pt-4">No submissions yet</div>
                            ) : submissions.map((s, i) => (
                                <div key={i} className="flex items-center justify-between py-2.5 border-b border-black/10 last:border-0 gap-2">
                                    <div className="flex flex-col min-w-0">
                                        <span className="text-coal text-sm font-medium truncate">{s.habboName}</span>
                                        <span className="text-coal/40 text-xs uppercase tracking-wide">Daily spins</span>
                                    </div>
                                    <div className="flex flex-col items-end flex-shrink-0">
                                        <span className="text-coal text-sm tabular-nums">{fmt(s.score)}</span>
                                        <span className="text-coal/40 text-xs">{s.stampsAwarded} framp{s.stampsAwarded !== 1 ? 's' : ''}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </ContentBox>

                    <ContentBox header="Note" theme="battleball" tint="red">
                        <div className="p-1 overflow-y-auto">
                            <div className="text-coal/70 text-sm">
                                Slimeburger Spin is in BETA and the artwork is placeholder. Framp Stakes gambles framps you already own - if you would rather not risk them, stick to the Daily Free Spins, which are always free and are the only mode that counts towards your score.
                            </div>
                        </div>
                    </ContentBox>
                </div>
            </div>

        </div>
    );
}
